interface AlertInsightMainCause {
  cause: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  explanation: string;
  diagnosticCriteria: string;
}

interface AlertInsightSolution {
  title: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  difficulty: 'Easy' | 'Medium' | 'Hard';
  estimatedTime: string;
  expectedEffect: string;
}

export interface AlertInsightData {
  type: 'event' | 'metric';
  threshold?: 'both' | 'high' | 'low';
  description: string;
  mainCauses: AlertInsightMainCause[];
  solutions: AlertInsightSolution[];
}

export type AlertInsightMap = Record<string, Record<string, AlertInsightData>>;

export const INSIGHT_THRESHOLD = {
  both: ['>=', '>', '<=', '<', '=='],
  high: ['>=', '>', '=='],
  low: ['<=', '<', '=='],
} as const;

const ALERT_INSIGHT_KO_DATA: AlertInsightMap = {
  application: {
    XM_OUTOFMEMORYERROR: {
      type: 'event',
      description:
        '애플리케이션에서 메모리 부족(OutOfMemoryError) 이벤트가 감지되었습니다.\nJVM Heap이 한계에 도달하여 새로운 객체를 할당할 수 없는 심각한 상태입니다.',
      mainCauses: [
        {
          cause: 'Heap 메모리 부족',
          priority: 'High',
          explanation: 'JVM 최대 Heap 크기(-Xmx) 설정 부족',
          diagnosticCriteria: 'Heap 사용률 98% 이상, -Xmx 설정이 실제 필요량보다 작음',
        },
        {
          cause: '메모리 누수',
          priority: 'High',
          explanation: '사용되지 않는 객체가 참조되어 GC되지 않고 누적',
          diagnosticCriteria: 'Full GC 후에도 Old Generation 사용률이 90% 이상',
        },
        {
          cause: '대용량 객체 생성',
          priority: 'Medium',
          explanation: '한번에 대량의 데이터를 로드하거나 큰 객체 생성',
          diagnosticCriteria: '특정 배치 작업 또는 대용량 조회 시 OOM 발생',
        },
        {
          cause: 'Metaspace 부족',
          priority: 'Low',
          explanation: '클래스 메타데이터 영역 부족 (Java 8 이상)',
          diagnosticCriteria: 'Metaspace 사용량이 MaxMetaspaceSize에 도달',
        },
      ],
      solutions: [
        {
          title: '긴급 재시작 및 -Xmx 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '서비스 정상화, OOM 재발 위험 감소',
        },
        {
          title: 'Heap Dump 분석 (OOM 발생 직전)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-4시간',
          expectedEffect: 'OOM 원인 정확히 파악',
        },
        {
          title: '메모리 누수 수정',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '3-10일',
          expectedEffect: '근본 원인 제거',
        },
      ],
    },
    XM_SOCKETTIMEOUTEXCEPTION: {
      type: 'event',
      description:
        '지정된 시간 내에 네트워크 응답이 없는 경우 발생하는 이벤트입니다.\n서버 지연, 네트워크 장애 등이 원인일 수 있습니다.',
      mainCauses: [
        {
          cause: '외부 API 응답 지연',
          priority: 'High',
          explanation: '연동된 외부 시스템의 응답이 Timeout 설정보다 느림',
          diagnosticCriteria: '특정 외부 API 호출 시 집중 발생',
        },
        {
          cause: '네트워크 장애',
          priority: 'High',
          explanation: '네트워크 품질 저하 또는 장애',
          diagnosticCriteria: '네트워크 Latency 증가, Packet Loss 발생',
        },
        {
          cause: 'Timeout 설정 부족',
          priority: 'Medium',
          explanation: 'Timeout 값이 너무 짧게 설정됨',
          diagnosticCriteria: '정상 응답시간보다 Timeout이 작음',
        },
        {
          cause: 'DB 응답 지연',
          priority: 'High',
          explanation: 'DB 쿼리 실행 시간이 Timeout 초과',
          diagnosticCriteria: 'SQL Elapse Time 증가',
        },
      ],
      solutions: [
        {
          title: 'Timeout 발생 API/서비스 식별',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '문제 서비스 파악',
        },
        {
          title: '외부 API 응답시간 모니터링 및 협의',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3일',
          expectedEffect: '외부 시스템 성능 개선',
        },
        {
          title: 'Timeout 설정 조정',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: '정상 응답에 대한 Timeout 오류 방지',
        },
        {
          title: 'Circuit Breaker 패턴 적용',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3일',
          expectedEffect: '외부 장애 전파 방지',
        },
      ],
    },
    XM_SOCKETEXCEPTION: {
      type: 'event',
      description:
        '네트워크 소켓에서 예기치 않은 오류가 발생했을 때 발생하는 이벤트입니다.\n연결 끊김, 잘못된 주소 사용 등이 원인일 수 있습니다.',
      mainCauses: [
        {
          cause: '연결 끊김',
          priority: 'High',
          explanation: '상대방 서버가 연결을 강제로 종료',
          diagnosticCriteria: 'Connection reset by peer 에러 메시지',
        },
        {
          cause: '방화벽 차단',
          priority: 'High',
          explanation: '방화벽 정책으로 연결 차단',
          diagnosticCriteria: '특정 IP/포트로의 연결 시도 실패',
        },
        {
          cause: '잘못된 호스트/포트',
          priority: 'Medium',
          explanation: '존재하지 않는 호스트 또는 포트로 연결 시도',
          diagnosticCriteria: 'Connection refused 에러',
        },
        {
          cause: '네트워크 불안정',
          priority: 'Medium',
          explanation: '네트워크 장비 문제로 간헐적 연결 끊김',
          diagnosticCriteria: '특정 시간대 또는 특정 구간에서 발생',
        },
      ],
      solutions: [
        {
          title: '에러 로그 및 스택 트레이스 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '정확한 오류 원인 파악',
        },
        {
          title: '대상 서버 연결 상태 확인',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '서버 장애 여부 확인',
        },
        {
          title: '방화벽 정책 확인 및 조정',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-4시간',
          expectedEffect: '통신 허용',
        },
        {
          title: '연결 재시도 로직 구현',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: '일시적 장애 대응',
        },
      ],
    },
    XM_JDBC_CONNECTION_FAIL: {
      type: 'event',
      description:
        '애플리케이션의 데이터베이스 연결이 실패하였습니다.\nConnection Pool이 고갈되거나 DB 응답 지연이 발생했습니다.',
      mainCauses: [
        {
          cause: 'Connection Pool 포화',
          priority: 'High',
          explanation: 'maxActive 설정값 초과로 신규 연결 불가',
          diagnosticCriteria: 'Active Connections가 maxPoolSize와 동일',
        },
        {
          cause: 'DB 서버 다운',
          priority: 'Critical',
          explanation: 'DB 서버 장애로 연결 불가',
          diagnosticCriteria: 'DB 서버 ping 실패, 연결 거부',
        },
        {
          cause: 'Connection Leak',
          priority: 'Medium',
          explanation: '사용된 Connection이 반환되지 않아 Pool 고갈',
          diagnosticCriteria: 'Connection이 특정 시간 이상 반환되지 않음',
        },
        {
          cause: '네트워크 장애',
          priority: 'High',
          explanation: 'DB 서버와의 네트워크 연결 불가',
          diagnosticCriteria: '네트워크 Timeout, Connection Refused',
        },
      ],
      solutions: [
        {
          title: 'DB 서버 상태 확인',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '5-10분',
          expectedEffect: 'DB 장애 여부 확인',
        },
        {
          title: 'Connection Pool 모니터링',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-20분',
          expectedEffect: 'Pool 고갈 여부 확인',
        },
        {
          title: 'Connection Pool 크기 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'Connection 부족 해소',
        },
        {
          title: 'Connection Leak 탐지 및 수정',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3일',
          expectedEffect: 'Pool 고갈 근본 원인 해결',
        },
      ],
    },
    XM_TXN_PLC: {
      type: 'event',
      description:
        '트랜잭션 처리 중 성능 저하 또는 대기 상태가 발생하는 경우입니다.\n리소스 경합이나 잠금(lock) 문제 등이 원인일 수 있습니다.',
      mainCauses: [
        {
          cause: 'Lock 대기',
          priority: 'High',
          explanation: 'DB Lock 또는 애플리케이션 Lock 대기',
          diagnosticCriteria: 'Thread State가 BLOCKED, Lock 관련 에러',
        },
        {
          cause: 'Slow Query',
          priority: 'High',
          explanation: 'DB 쿼리 실행 시간 지연',
          diagnosticCriteria: 'SQL Elapse Time 증가',
        },
        {
          cause: '리소스 경합',
          priority: 'Medium',
          explanation: '공유 리소스 접근 경합',
          diagnosticCriteria: '동시 접근 시 지연 발생',
        },
      ],
      solutions: [
        {
          title: 'Thread Dump 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'Lock 대기 구간 식별',
        },
        {
          title: 'DB Lock 상태 확인',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '20-40분',
          expectedEffect: 'DB Lock 원인 파악',
        },
        {
          title: '쿼리 최적화 및 Lock 범위 최소화',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2-5일',
          expectedEffect: 'Lock 대기 시간 감소',
        },
      ],
    },
    XM_JDBC_TOOMANYFETCH: {
      type: 'event',
      description:
        '한 번에 가져오는 데이터 양이 너무 많아 성능 저하가 발생하는 경우입니다.\n불필요한 데이터 조회를 줄이거나 쿼리를 최적화해야 합니다.',
      mainCauses: [
        {
          cause: '페이징 미적용',
          priority: 'High',
          explanation: '전체 데이터를 한 번에 조회',
          diagnosticCriteria: 'LIMIT 절 없는 SELECT 쿼리',
        },
        {
          cause: '불필요한 컬럼 조회',
          priority: 'Medium',
          explanation: 'SELECT * 사용으로 모든 컬럼 조회',
          diagnosticCriteria: '실제 사용 컬럼은 일부',
        },
        {
          cause: 'Fetch Size 설정 부적절',
          priority: 'Low',
          explanation: 'JDBC Fetch Size가 너무 큼',
          diagnosticCriteria: 'Fetch Size > 1000',
        },
      ],
      solutions: [
        {
          title: '페이징 적용',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2일',
          expectedEffect: '메모리 사용량 및 응답시간 80% 감소',
        },
        {
          title: '필요한 컬럼만 조회 (SELECT 절 최적화)',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-3일',
          expectedEffect: '네트워크 트래픽 30-50% 감소',
        },
        {
          title: 'Fetch Size 조정',
          priority: 'Low',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: 'DB-WAS 간 Round Trip 최적화',
        },
      ],
    },
    app_was_cpu_usage: {
      type: 'metric',
      threshold: 'high',
      description:
        '애플리케이션의 CPU 사용률이 임계치를 초과했습니다.\n과도한 CPU 사용은 응답 지연 및 시스템 전체 성능 저하를 유발할 수 있습니다.',
      mainCauses: [
        {
          cause: '무한 루프 또는 과도한 반복',
          priority: 'High',
          explanation: '비효율적인 알고리즘이나 무한 루프로 인한 CPU 과부하',
          diagnosticCriteria:
            '특정 스레드의 CPU 사용률이 100% 근접, Thread Dump에서 동일 스택이 반복적으로 나타남',
        },
        {
          cause: 'GC 과부하',
          priority: 'High',
          explanation: '빈번한 Full GC로 인한 CPU 자원 소모',
          diagnosticCriteria: 'GC Time이 전체 실행 시간의 10% 이상, Full GC가 1분에 1회 이상 발생',
        },
        {
          cause: '대량 트래픽 처리',
          priority: 'Medium',
          explanation: '동시 요청 급증으로 인한 Thread 증가 및 Context Switching',
          diagnosticCriteria: 'RUNNABLE 상태의 Thread가 CPU Core 수의 2배 이상',
        },
        {
          cause: '정규표현식 과다 사용',
          priority: 'Low',
          explanation: '복잡한 정규식 패턴 매칭으로 인한 CPU 소모',
          diagnosticCriteria: 'Pattern.matches() 메소드가 CPU 샘플의 20% 이상 차지',
        },
      ],
      solutions: [
        {
          title: '프로파일링 도구로 CPU 사용률 높은 메소드 식별',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2시간',
          expectedEffect: '병목 지점 정확히 파악 및 타겟 최적화',
        },
        {
          title: '알고리즘 복잡도 개선 및 불필요한 연산 제거',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '1-3일',
          expectedEffect: 'CPU 사용률 50-80% 감소',
        },
        {
          title: 'GC 튜닝 및 Heap 메모리 크기 적정화',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: '동시성 개선, 응답 시간 20-40% 단축',
        },
      ],
    },
    app_was_free_memory: {
      type: 'metric',
      threshold: 'low',
      description:
        '애플리케이션의 메모리 사용률이 임계치를 초과했습니다.\n지속적인 메모리 증가는 성능 저하 및 OOM으로 이어질 수 있습니다.',
      mainCauses: [
        {
          cause: '메모리 누수',
          priority: 'High',
          explanation: '사용하지 않는 객체가 GC되지 않고 계속 Heap에 남아있음',
          diagnosticCriteria:
            'Old Generation 사용량이 지속적으로 증가, Full GC 후에도 메모리 사용률이 80% 이상 유지',
        },
        {
          cause: '캐시 과다 사용',
          priority: 'Medium',
          explanation: 'Local Cache 크기 제한 없이 무한정 증가',
          diagnosticCriteria: '특정 캐시 객체가 Heap의 30% 이상 차지',
        },
        {
          cause: '대량 트래픽 유입',
          priority: 'Low',
          explanation: '갑작스런 요청 증가로 인한 일시적 메모리 급증',
          diagnosticCriteria: '특정 시간대에만 메모리 급증, Young GC로 회수됨',
        },
      ],
      solutions: [
        {
          title: 'GC 로그 분석 및 튜닝',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2시간',
          expectedEffect: 'GC 효율성 향상, Pause Time 30-50% 감소',
        },
        {
          title: 'Heap Dump 분석으로 메모리 누수 객체 식별',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-4시간',
          expectedEffect: '메모리 누수 원인 파악',
        },
        {
          title: '메모리 누수 코드 수정',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2-7일',
          expectedEffect: '메모리 사용량 안정화',
        },
        {
          title: '-Xmx 크기 증설 (임시 조치)',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'OOM 위험 일시적 회피',
        },
      ],
    },
    app_active_transaction_count: {
      type: 'metric',
      threshold: 'high',
      description:
        '활성 트랜잭션이 임계치를 초과했습니다.\n트랜잭션 적체로 인한 응답 지연이 발생하고 있습니다.',
      mainCauses: [
        {
          cause: 'Slow Transaction 증가',
          priority: 'High',
          explanation: '특정 트랜잭션의 응답시간이 길어져 완료되지 않고 적체',
          diagnosticCriteria: 'Transaction Elapse Time이 평소 대비 3배 이상',
        },
        {
          cause: 'DB 또는 외부 API 지연',
          priority: 'High',
          explanation: 'DB 쿼리 또는 외부 API 호출 지연으로 트랜잭션 완료 지연',
          diagnosticCriteria: 'SQL Elapse Time 또는 Remote Call Elapse Time 증가',
        },
        {
          cause: '동시 사용자 급증',
          priority: 'Medium',
          explanation: '갑작스런 트래픽 증가로 동시 처리 트랜잭션 수 증가',
          diagnosticCriteria: 'Active Users가 평소 대비 2배 이상, TPS 급증',
        },
      ],
      solutions: [
        {
          title: '현재 활성 트랜잭션 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '병목 트랜잭션 식별',
        },
        {
          title: 'Slow Transaction 쿼리 최적화',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3일',
          expectedEffect: '트랜잭션 응답시간 50-70% 개선',
        },
        {
          title: 'Thread Pool 크기 조정',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: '동시 처리 용량 증가',
        },
      ],
    },
    app_tps: {
      type: 'metric',
      threshold: 'both',
      description:
        '초당 트랜잭션 처리량이 정상 범위를 벗어났습니다.\n급격한 증가는 트래픽 폭증, 급격한 감소는 시스템 장애를 의미할 수 있습니다.',
      mainCauses: [
        {
          cause: '트래픽 급증',
          priority: 'High',
          explanation: '이벤트, 마케팅 캠페인 등으로 인한 급격한 사용자 유입',
          diagnosticCriteria: 'TPS가 평소 대비 200% 이상 증가',
        },
        {
          cause: 'DDoS 공격',
          priority: 'Critical',
          explanation: '악의적인 대량 요청으로 인한 TPS 비정상 증가',
          diagnosticCriteria: 'TPS 급증하나 비정상 패턴, Error Rate 증가',
        },
        {
          cause: '시스템 장애 (TPS 급락)',
          priority: 'Critical',
          explanation: 'WAS 또는 DB 장애로 트랜잭션 처리 불가',
          diagnosticCriteria: 'TPS가 평소 대비 50% 이하, Error Rate 증가',
        },
      ],
      solutions: [
        {
          title: '트래픽 패턴 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '정상/비정상 트래픽 구분',
        },
        {
          title: 'Auto Scaling 적용',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: '트래픽 증가에 자동 대응',
        },
        {
          title: 'Rate Limiting 적용',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '4-8시간',
          expectedEffect: 'DDoS 및 과도한 요청 차단',
        },
      ],
    },
    app_transaction_count: {
      type: 'metric',
      threshold: 'both',
      description:
        '분당 트랜잭션 처리 총량이 비정상적입니다.\n급격한 변화는 시스템 문제를 의미할 수 있습니다.',
      mainCauses: [
        {
          cause: '피크 시간대 도래',
          priority: 'Low',
          explanation: '정기적인 피크 시간으로 인한 자연스러운 증가',
          diagnosticCriteria: '주기적 패턴, Error Rate는 정상',
        },
        {
          cause: '배치 작업 실행',
          priority: 'Medium',
          explanation: '대량 배치 작업 실행으로 인한 Throughput 급증',
          diagnosticCriteria: '특정 시간대 집중, 특정 Transaction Type 집중',
        },
        {
          cause: 'WAS 인스턴스 다운',
          priority: 'Critical',
          explanation: '일부 WAS 인스턴스 장애로 전체 처리량 감소',
          diagnosticCriteria: 'Active WAS 수 감소, Error Rate 증가',
        },
      ],
      solutions: [
        {
          title: 'WAS 인스턴스 상태 확인',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '5-10분',
          expectedEffect: '장애 인스턴스 식별',
        },
        {
          title: '배치 작업 스케줄 조정',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: '피크 시간 분산',
        },
        {
          title: '로드 밸런싱 정책 검토',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-4시간',
          expectedEffect: '부하 균등 분배',
        },
      ],
    },
    app_transaction_elapse_time_avg: {
      type: 'metric',
      threshold: 'high',
      description:
        '평균 트랜잭션 응답시간이 임계치를 초과했습니다.\n사용자 경험 저하 및 성능 문제가 발생하고 있습니다.',
      mainCauses: [
        {
          cause: 'DB 쿼리 성능 저하',
          priority: 'High',
          explanation: 'Slow Query 증가로 인한 전체 응답시간 증가',
          diagnosticCriteria: 'SQL Elapse Time Avg 증가',
        },
        {
          cause: '외부 API 응답 지연',
          priority: 'High',
          explanation: '연동된 외부 시스템의 응답 지연',
          diagnosticCriteria: 'Remote Call Elapse Time 증가',
        },
        {
          cause: 'GC 과부하',
          priority: 'Medium',
          explanation: '빈번한 GC로 인한 애플리케이션 응답 지연',
          diagnosticCriteria: 'GC Time 증가, Heap Usage 높음',
        },
      ],
      solutions: [
        {
          title: 'APM 트레이스 분석으로 병목 구간 식별',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: '응답시간 지연 구간 정확히 파악',
        },
        {
          title: 'Slow Query 최적화',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '1-5일',
          expectedEffect: '응답시간 40-60% 개선',
        },
        {
          title: 'Cache 적용',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-3일',
          expectedEffect: 'DB 부하 감소, 응답시간 30-50% 개선',
        },
      ],
    },
    app_transaction_elapse_time: {
      type: 'metric',
      threshold: 'high',
      description:
        '트랜잭션 응답시간 합계가 비정상적으로 증가했습니다.\n전체적인 시스템 성능 저하를 나타냅니다.',
      mainCauses: [
        {
          cause: '트랜잭션 처리량 증가',
          priority: 'Medium',
          explanation: '정상적인 트래픽 증가로 인한 합계 증가',
          diagnosticCriteria: 'TPS 증가, 평균 응답시간은 정상',
        },
        {
          cause: '개별 트랜잭션 지연',
          priority: 'High',
          explanation: '각 트랜잭션의 응답시간이 증가',
          diagnosticCriteria: '평균 응답시간도 함께 증가',
        },
        {
          cause: '특정 트랜잭션 적체',
          priority: 'High',
          explanation: '일부 느린 트랜잭션이 전체 합계를 증가시킴',
          diagnosticCriteria: '특정 URL 패턴에서 극단적으로 높은 응답시간',
        },
      ],
      solutions: [
        {
          title: '트랜잭션별 응답시간 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '20-40분',
          expectedEffect: '문제 트랜잭션 식별',
        },
        {
          title: '느린 트랜잭션 최적화',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5일',
          expectedEffect: '전체 응답시간 합계 30-50% 감소',
        },
      ],
    },
    app_transaction_exception_count: {
      type: 'metric',
      threshold: 'high',
      description:
        '트랜잭션 에러가 임계치를 초과했습니다.\n비즈니스 로직 오류, 시스템 장애 등이 발생하고 있습니다.',
      mainCauses: [
        {
          cause: 'DB 연결 실패',
          priority: 'Critical',
          explanation: 'DB Connection Pool 고갈 또는 DB 서버 장애',
          diagnosticCriteria: 'JDBC Exception 다수 발생',
        },
        {
          cause: '외부 API 장애',
          priority: 'High',
          explanation: '연동된 외부 시스템 장애로 인한 에러',
          diagnosticCriteria: 'Timeout Exception, Connection Refused 증가',
        },
      ],
      solutions: [
        {
          title: '에러 로그 및 스택 트레이스 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '에러 원인 파악',
        },
        {
          title: '긴급 롤백 (배포 후 발생 시)',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '서비스 정상화',
        },
        {
          title: '버그 수정 및 재배포',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '수시간-수일',
          expectedEffect: '근본 원인 해결',
        },
      ],
    },
    app_txn_error_ratio: {
      type: 'metric',
      threshold: 'high',
      description:
        '전체 트랜잭션 대비 에러 비율이 임계치를 초과했습니다.\n서비스 품질이 저하되고 있습니다.',
      mainCauses: [
        {
          cause: '시스템 리소스 부족',
          priority: 'High',
          explanation: 'CPU, Memory, Thread 부족으로 요청 처리 실패',
          diagnosticCriteria: 'CPU Usage > 90%, Memory 부족',
        },
        {
          cause: '의존 서비스 장애',
          priority: 'High',
          explanation: 'DB, Cache, 외부 API 등 의존 서비스 장애',
          diagnosticCriteria: '특정 서비스 호출 시 집중 에러',
        },
        {
          cause: '코드 품질 문제',
          priority: 'Medium',
          explanation: 'Exception 처리 미흡, Edge Case 처리 부족',
          diagnosticCriteria: '다양한 Exception 타입 발생',
        },
      ],
      solutions: [
        {
          title: 'Error 패턴 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: '공통 원인 식별',
        },
        {
          title: '리소스 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-4시간',
          expectedEffect: '시스템 여유 확보',
        },
      ],
    },
    app_txn_error_count: {
      type: 'metric',
      threshold: 'high',
      description: '에러 트랜잭션 발생량이 임계치를 초과했습니다.',
      mainCauses: [
        {
          cause: '전체 트래픽 증가',
          priority: 'High',
          explanation: '트래픽 증가에 비례한 에러 증가',
          diagnosticCriteria: 'TPS 증가, 에러율은 정상 범위',
        },
        {
          cause: '특정 기능 장애',
          priority: 'Medium',
          explanation: '일부 기능에서 지속적으로 에러 발생',
          diagnosticCriteria: '특정 API 엔드포인트 집중',
        },
      ],
      solutions: [
        {
          title: '에러 발생 API 엔드포인트 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '15-30분',
          expectedEffect: '문제 기능 식별',
        },
        {
          title: '해당 기능 긴급 패치',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1-8시간',
          expectedEffect: '에러 발생 중단',
        },
      ],
    },
    app_remote_elapse: {
      type: 'metric',
      threshold: 'high',
      description:
        'Remote Call 응답시간 합계가 증가했습니다.\n외부 시스템 연동 성능 저하를 나타냅니다.',
      mainCauses: [
        {
          cause: '외부 API 응답 지연',
          priority: 'High',
          explanation: '연동된 외부 시스템의 성능 저하',
          diagnosticCriteria: 'Remote Call 평균 응답시간 증가',
        },
        {
          cause: 'Remote Call 호출 빈도 증가',
          priority: 'Medium',
          explanation: '비즈니스 로직 변경으로 외부 호출 증가',
          diagnosticCriteria: 'Remote Call Count 증가',
        },
        {
          cause: '네트워크 지연',
          priority: 'Medium',
          explanation: '네트워크 품질 저하로 인한 지연',
          diagnosticCriteria: '네트워크 Latency 증가',
        },
      ],
      solutions: [
        {
          title: '외부 API 응답시간 모니터링',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-20분',
          expectedEffect: '지연 발생 외부 시스템 식별',
        },
        {
          title: 'Timeout 설정 및 Circuit Breaker 적용',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: '장애 전파 방지',
        },
        {
          title: 'Cache 적용 (가능한 경우)',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3일',
          expectedEffect: '외부 호출 빈도 감소',
        },
      ],
    },
    app_sql_exec_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'SQL 실행 횟수가 임계치를 초과했습니다.\nDB 부하 증가 및 성능 저하가 발생할 수 있습니다.',
      mainCauses: [
        {
          cause: 'N+1 Query 문제',
          priority: 'High',
          explanation: '반복문 내에서 개별 쿼리 실행으로 쿼리 수 폭증',
          diagnosticCriteria: '특정 트랜잭션에서 동일 쿼리 다수 실행',
        },
        {
          cause: '트래픽 증가',
          priority: 'Medium',
          explanation: '정상적인 트래픽 증가로 인한 쿼리 증가',
          diagnosticCriteria: 'TPS 증가와 비례',
        },
        {
          cause: '비효율적 데이터 조회',
          priority: 'Medium',
          explanation: '필요 이상의 데이터를 여러 번 조회',
          diagnosticCriteria: '동일 데이터를 반복 조회',
        },
      ],
      solutions: [
        {
          title: 'SQL 실행 패턴 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'N+1 Query 식별',
        },
        {
          title: 'Query 최적화 (JOIN, Batch Query)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3일',
          expectedEffect: 'SQL 실행 횟수 70-90% 감소',
        },
        {
          title: 'Cache 적용',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3일',
          expectedEffect: 'DB 부하 30-50% 감소',
        },
      ],
    },
    app_sql_prepare_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'SQL Prepare 횟수가 과다합니다.\nPreparedStatement 재사용 부족 또는 쿼리 생성 문제가 있습니다.',
      mainCauses: [
        {
          cause: 'PreparedStatement 미재사용',
          priority: 'High',
          explanation: '매번 새로운 PreparedStatement 생성',
          diagnosticCriteria: 'Prepare Count와 Execute Count 비율 1:1 근접',
        },
        {
          cause: 'Connection Pool 설정 부적절',
          priority: 'Medium',
          explanation: 'Connection이 자주 생성/해제됨',
          diagnosticCriteria: 'Connection 재사용률 낮음',
        },
      ],
      solutions: [
        {
          title: 'PreparedStatement 재사용 구조 개선',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5일',
          expectedEffect: 'Prepare 횟수 50-70% 감소',
        },
        {
          title: 'Connection Pool 설정 최적화',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: 'Connection 재사용률 향상',
        },
      ],
    },
    app_sql_fetch_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'SQL Fetch 횟수가 매우 많습니다.\n대량의 데이터를 조회하고 있어 성능 저하 및 메모리 과다 사용이 발생할 수 있습니다.',
      mainCauses: [
        {
          cause: '페이징 미적용',
          priority: 'High',
          explanation: '전체 데이터를 한 번에 조회',
          diagnosticCriteria: 'LIMIT 절 없는 쿼리, ResultSet 크기 매우 큼',
        },
        {
          cause: '불필요한 컬럼 조회',
          priority: 'Medium',
          explanation: 'SELECT * 사용으로 모든 컬럼 조회',
          diagnosticCriteria: '실제 사용하는 컬럼은 일부',
        },
        {
          cause: 'Batch 처리 부족',
          priority: 'Medium',
          explanation: '대량 데이터 처리 시 적절한 Batch 미적용',
          diagnosticCriteria: '단건씩 Fetch',
        },
      ],
      solutions: [
        {
          title: '페이징 적용',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2일',
          expectedEffect: 'Fetch 횟수 및 메모리 사용량 80% 감소',
        },
        {
          title: '필요한 컬럼만 조회 (SELECT 절 최적화)',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-3일',
          expectedEffect: '네트워크 트래픽 및 메모리 30-50% 감소',
        },
        {
          title: 'Fetch Size 조정',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: 'DB-WAS 간 Round Trip 감소',
        },
      ],
    },
    app_sql_elapse_time_avg: {
      type: 'metric',
      threshold: 'high',
      description:
        '평균 SQL 실행시간이 지연되고 있습니다.\n쿼리 최적화 또는 DB 리소스 확인이 필요합니다.',
      mainCauses: [
        {
          cause: '인덱스 부재',
          priority: 'High',
          explanation: 'WHERE 조건에 인덱스가 없어 Full Table Scan 발생',
          diagnosticCriteria: '실행 계획에서 Table Scan',
        },
        {
          cause: '복잡한 JOIN',
          priority: 'High',
          explanation: '다중 JOIN으로 인한 쿼리 복잡도 증가',
          diagnosticCriteria: 'JOIN 3개 이상, 대용량 테이블',
        },
        {
          cause: 'DB 리소스 부족',
          priority: 'Medium',
          explanation: 'DB CPU, Memory, Disk I/O 부족',
          diagnosticCriteria: 'DB 서버 리소스 사용률 80% 이상',
        },
      ],
      solutions: [
        {
          title: 'Slow Query 분석 및 실행 계획 확인',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: '문제 쿼리 식별',
        },
        {
          title: '필요한 컬럼만 조회 (SELECT 절 최적화)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: '쿼리 실행시간 70-90% 개선',
        },
        {
          title: '쿼리 리팩토링 (JOIN 최소화, 서브쿼리 제거)',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '3-7일',
          expectedEffect: '쿼리 성능 50-80% 개선',
        },
      ],
    },
    app_sql_elapse_time: {
      type: 'metric',
      threshold: 'high',
      description: 'SQL 실행시간 합계가 증가했습니다.\n전체적인 DB 부하 증가를 나타냅니다.',
      mainCauses: [
        {
          cause: 'SQL 실행 횟수 증가',
          priority: 'Medium',
          explanation: '트래픽 증가로 쿼리 실행 빈도 증가',
          diagnosticCriteria: 'SQL Execute Count 증가',
        },
        {
          cause: '개별 쿼리 성능 저하',
          priority: 'High',
          explanation: '각 쿼리의 실행시간이 증가',
          diagnosticCriteria: 'SQL Elapse Avg 증가',
        },
      ],
      solutions: [
        {
          title: 'SQL 실행 패턴 및 성능 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: '원인 파악 (빈도 vs 성능)',
        },
        {
          title: '쿼리 최적화',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5일',
          expectedEffect: '전체 SQL 시간 40-60% 감소',
        },
      ],
    },
    app_connection_time: {
      type: 'metric',
      threshold: 'high',
      description: 'DB 연결시간 합계가 증가했습니다.\nConnection 획득 지연이 발생하고 있습니다.',
      mainCauses: [
        {
          cause: 'Connection Pool 부족',
          priority: 'High',
          explanation: 'Pool에서 Connection 획득 대기 시간 증가',
          diagnosticCriteria: 'Active Connections가 Max Pool Size 근접',
        },
        {
          cause: 'DB 네트워크 지연',
          priority: 'Medium',
          explanation: 'DB 서버와의 네트워크 지연',
          diagnosticCriteria: '네트워크 Latency 증가',
        },
        {
          cause: 'Connection Leak',
          priority: 'High',
          explanation: '사용 후 반환하지 않은 Connection으로 Pool 고갈',
          diagnosticCriteria: 'Connection 사용 후 미반환',
        },
      ],
      solutions: [
        {
          title: 'Connection Pool 모니터링',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-20분',
          expectedEffect: 'Pool 상태 파악',
        },
        {
          title: 'Connection Pool 크기 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'Connection 대기 시간 감소',
        },
        {
          title: 'Connection Leak 탐지 및 수정',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1-3일',
          expectedEffect: 'Pool 고갈 방지',
        },
      ],
    },
    app_http_call: {
      type: 'metric',
      threshold: 'high',
      description: 'Remote Call 호출 횟수가 증가했습니다.\n외부 시스템 의존도가 높아지고 있습니다.',
      mainCauses: [
        {
          cause: '비즈니스 로직 변경',
          priority: 'Medium',
          explanation: '신규 기능으로 외부 API 호출 증가',
          diagnosticCriteria: '특정 기능 배포 후 발생',
        },
        {
          cause: 'N+1 Remote Call',
          priority: 'High',
          explanation: '반복문 내에서 외부 API 호출',
          diagnosticCriteria: '동일 API를 여러 번 호출',
        },
        {
          cause: '트래픽 증가',
          priority: 'Low',
          explanation: '정상적인 트래픽 증가에 비례',
          diagnosticCriteria: 'TPS 증가와 비례',
        },
      ],
      solutions: [
        {
          title: 'Remote Call 패턴 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'N+1 문제 식별',
        },
        {
          title: 'Batch API 호출로 변경',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-4일',
          expectedEffect: 'Remote Call 횟수 70-90% 감소',
        },
        {
          title: 'Cache 적용',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3일',
          expectedEffect: '외부 호출 30-50% 감소',
        },
      ],
    },
    app_active_user: {
      type: 'metric',
      threshold: 'high',
      description: '활성 사용자 수가 급증했습니다.\n트래픽 폭증 또는 이벤트성 유입이 발생했습니다.',
      mainCauses: [
        {
          cause: '마케팅 캠페인',
          priority: 'Low',
          explanation: '계획된 이벤트로 인한 자연스러운 증가',
          diagnosticCriteria: '사전 공지된 이벤트 시간대',
        },
        {
          cause: '미디어 노출',
          priority: 'Medium',
          explanation: '뉴스, SNS 등을 통한 급격한 유입',
          diagnosticCriteria: '예상치 못한 트래픽 급증',
        },
        {
          cause: 'Bot 공격',
          priority: 'High',
          explanation: '자동화된 Bot에 의한 비정상 접속',
          diagnosticCriteria: '비정상적인 접속 패턴',
        },
      ],
      solutions: [
        {
          title: '트래픽 소스 및 패턴 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '정상/비정상 트래픽 구분',
        },
        {
          title: 'Auto Scaling 활성화',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '즉시',
          expectedEffect: '시스템 용량 자동 확장',
        },
        {
          title: 'Bot 차단 (비정상인 경우)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-4시간',
          expectedEffect: '비정상 트래픽 차단',
        },
      ],
    },
    app_concurrent_user: {
      type: 'metric',
      threshold: 'high',
      description: '동시 접속 사용자 수가 급증했습니다.\n시스템 리소스 부족이 발생할 수 있습니다.',
      mainCauses: [
        {
          cause: '피크 시간대',
          priority: 'Low',
          explanation: '정기적인 피크 시간 (점심, 저녁 등)',
          diagnosticCriteria: '주기적 패턴',
        },
        {
          cause: '이벤트 시작',
          priority: 'Medium',
          explanation: '한정판 판매, 티켓 오픈 등 동시 집중',
          diagnosticCriteria: '특정 시각에 집중',
        },
        {
          cause: '세션 Timeout 설정 과다',
          priority: 'Medium',
          explanation: '세션이 오래 유지되어 누적',
          diagnosticCriteria: '세션 Timeout이 너무 김',
        },
      ],
      solutions: [
        {
          title: '세션 Timeout 설정 검토',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: '불필요한 세션 정리',
        },
        {
          title: 'Session Storage 확장 (Redis 등)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: '세션 처리 용량 증가',
        },
      ],
    },
    app_apdex: {
      type: 'metric',
      threshold: 'low',
      description:
        'Apdex(Application Performance Index) 점수가 저하되었습니다.\n사용자 경험이 나빠지고 있습니다.',
      mainCauses: [
        {
          cause: '응답시간 증가',
          priority: 'High',
          explanation: 'Transaction Elapse Time 증가로 만족도 저하',
          diagnosticCriteria: '평균 응답시간 > 3초',
        },
        {
          cause: '에러 발생 증가',
          priority: 'High',
          explanation: '에러로 인한 사용자 불만족',
          diagnosticCriteria: 'Error Transaction Ratio > 1%',
        },
        {
          cause: '타임아웃 증가',
          priority: 'Medium',
          explanation: '요청이 완료되지 않고 타임아웃',
          diagnosticCriteria: 'Timeout Exception 증가',
        },
      ],
      solutions: [
        {
          title: 'Apdex Bad Count 트랜잭션 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: '불만족 원인 식별',
        },
        {
          title: '응답시간 개선 (쿼리 최적화, Cache 등)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5일',
          expectedEffect: 'Apdex 점수 향상',
        },
      ],
    },
    app_apdex_excellent: {
      type: 'metric',
      threshold: 'low',
      description:
        '우수 응답 트랜잭션 수가 감소했습니다.\n빠른 응답을 제공하는 트랜잭션이 줄어들고 있습니다.',
      mainCauses: [
        {
          cause: '전반적인 성능 저하',
          priority: 'High',
          explanation: '시스템 전체 응답시간이 느려짐',
          diagnosticCriteria: '평균 응답시간 증가',
        },
        {
          cause: '트래픽 증가',
          priority: 'Medium',
          explanation: '부하 증가로 응답시간 증가',
          diagnosticCriteria: 'TPS 증가',
        },
      ],
      solutions: [
        {
          title: '성능 저하 원인 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2시간',
          expectedEffect: '병목 구간 파악',
        },
        {
          title: '시스템 최적화',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '3-7일',
          expectedEffect: 'Excellent Count 증가',
        },
      ],
    },
    app_apdex_warning: {
      type: 'metric',
      threshold: 'high',
      description:
        '경고 수준 응답 트랜잭션이 증가했습니다.\n응답시간이 목표치를 초과하고 있습니다.',
      mainCauses: [
        {
          cause: '일부 트랜잭션 지연',
          priority: 'High',
          explanation: '특정 트랜잭션이 느려짐',
          diagnosticCriteria: '특정 URL 패턴에서 지연',
        },
        {
          cause: '시스템 리소스 부족',
          priority: 'Medium',
          explanation: 'CPU, Memory 부족으로 응답 지연',
          diagnosticCriteria: '리소스 사용률 80% 이상',
        },
      ],
      solutions: [
        {
          title: 'Warning 수준 트랜잭션 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: '지연 트랜잭션 식별',
        },
        {
          title: '해당 트랜잭션 최적화',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5일',
          expectedEffect: 'Warning Count 감소',
        },
      ],
    },
    app_apdex_bad: {
      type: 'metric',
      threshold: 'high',
      description: '불량 응답 트랜잭션이 증가했습니다.\n심각한 응답 지연이 발생하고 있습니다.',
      mainCauses: [
        {
          cause: '심각한 성능 문제',
          priority: 'Critical',
          explanation: '시스템 장애 수준의 응답 지연',
          diagnosticCriteria: '응답시간 > 12초 (Apdex T의 4배)',
        },
        {
          cause: '특정 기능 장애',
          priority: 'High',
          explanation: '일부 기능에서 극심한 지연',
          diagnosticCriteria: '특정 URL에서 집중 발생',
        },
      ],
      solutions: [
        {
          title: 'Bad Count 트랜잭션 긴급 분석',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: '긴급 조치 필요 항목 식별',
        },
        {
          title: '긴급 조치 (해당 기능 일시 중단 고려)',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '즉시',
          expectedEffect: '전체 시스템 보호',
        },
      ],
    },
    app_was_memory_total: {
      type: 'metric',
      threshold: 'high',
      description: '애플리케이션 전체 메모리 사용량이 과다합니다.',
      mainCauses: [
        {
          cause: '메모리 누수',
          priority: 'High',
          explanation: '사용하지 않는 객체가 메모리에 계속 유지됨',
          diagnosticCriteria: '메모리 사용량 지속 증가',
        },
        {
          cause: '대용량 데이터 처리',
          priority: 'Medium',
          explanation: '한 번에 많은 데이터를 메모리에 로드',
          diagnosticCriteria: '특정 작업 수행 시 메모리 급증',
        },
      ],
      solutions: [
        {
          title: 'Heap Dump 분석',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3시간',
          expectedEffect: '메모리 사용 객체 식별',
        },
        {
          title: '메모리 누수 수정',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2-7일',
          expectedEffect: '메모리 사용량 안정화',
        },
      ],
    },
    app_was_memory_rss: {
      type: 'metric',
      threshold: 'high',
      description:
        '애플리케이션 RSS(Resident Set Size) 메모리가 과다합니다.\n실제 물리 메모리 사용량이 높습니다.',
      mainCauses: [
        {
          cause: 'Heap 외 메모리 사용 증가',
          priority: 'High',
          explanation: 'Native Memory, Direct Buffer 등 사용',
          diagnosticCriteria: 'Heap은 정상이나 RSS 높음',
        },
        {
          cause: 'JVM Off-Heap 메모리',
          priority: 'Medium',
          explanation: 'Metaspace, Code Cache 등 증가',
          diagnosticCriteria: 'Metaspace 사용량 증가',
        },
      ],
      solutions: [
        {
          title: 'Native Memory Tracking 활성화',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: 'Off-Heap 메모리 사용 파악',
        },
        {
          title: 'Direct Buffer 사용 검토',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-4일',
          expectedEffect: 'Native Memory 사용 최적화',
        },
      ],
    },
    app_pod_cpu_usage: {
      type: 'metric',
      threshold: 'high',
      description:
        'Kubernetes Pod의 CPU 사용률이 과다합니다.\nPod 리소스 제한에 도달할 수 있습니다.',
      mainCauses: [
        {
          cause: 'CPU Limit 설정 부족',
          priority: 'High',
          explanation: 'Pod에 할당된 CPU가 부족',
          diagnosticCriteria: 'CPU Request/Limit이 실제 필요량보다 작음',
        },
        {
          cause: '애플리케이션 CPU 과다 사용',
          priority: 'High',
          explanation: '애플리케이션 자체의 CPU 사용 문제',
          diagnosticCriteria: 'Application CPU Usage도 높음',
        },
      ],
      solutions: [
        {
          title: 'Pod CPU Limit 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'CPU Throttling 방지',
        },
        {
          title: 'HPA(Horizontal Pod Autoscaler) 설정',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-2일',
          expectedEffect: '부하에 따른 자동 스케일링',
        },
      ],
    },
    app_pod_memory_usage: {
      type: 'metric',
      threshold: 'high',
      description: 'Kubernetes Pod의 메모리 사용률이 과다합니다.\nOOMKilled 위험이 있습니다.',
      mainCauses: [
        {
          cause: 'Memory Limit 설정 부족',
          priority: 'High',
          explanation: 'Pod에 할당된 메모리가 부족',
          diagnosticCriteria: 'Memory Limit이 실제 필요량보다 작음',
        },
        {
          cause: '애플리케이션 메모리 누수',
          priority: 'High',
          explanation: '애플리케이션의 메모리 누수',
          diagnosticCriteria: 'Application Memory도 높음',
        },
      ],
      solutions: [
        {
          title: 'Pod Memory Limit 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'OOMKilled 방지',
        },
        {
          title: '애플리케이션 메모리 최적화',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2-7일',
          expectedEffect: '근본적인 메모리 사용량 감소',
        },
      ],
    },
    app_db_active_connection_count: {
      type: 'metric',
      threshold: 'high',
      description: '활성 DB 커넥션 수가 과다합니다. Connection Pool 고갈 위험이 있습니다.',
      mainCauses: [
        {
          cause: 'Long Running Query',
          priority: 'High',
          explanation: '오래 실행되는 쿼리로 Connection 점유 시간 증가',
          diagnosticCriteria: 'SQL Elapse Time 높음',
        },
        {
          cause: 'Connection Leak',
          priority: 'Critical',
          explanation: 'Connection 사용 후 미반환',
          diagnosticCriteria: 'Connection이 계속 증가',
        },
        {
          cause: '트래픽 증가',
          priority: 'Medium',
          explanation: '요청 증가로 DB Connection 필요량 증가',
          diagnosticCriteria: 'TPS 증가',
        },
      ],
      solutions: [
        {
          title: '현재 활성 Connection 분석',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30분',
          expectedEffect: 'Long Running Query 식별',
        },
        {
          title: 'Connection Leak 점검 및 수정',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1-3일',
          expectedEffect: 'Connection Pool 안정화',
        },
        {
          title: 'Connection Pool 크기 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'Pool 여유 확보',
        },
      ],
    },
    app_db_total_connection_count: {
      type: 'metric',
      threshold: 'high',
      description:
        '전체 DB 커넥션 수가 Pool Size에 근접했습니다.\n신규 Connection 생성 불가 위험이 있습니다.',
      mainCauses: [
        {
          cause: 'Connection Pool 크기 부족',
          priority: 'High',
          explanation: '실제 필요량보다 Pool Size가 작음',
          diagnosticCriteria: '자주 Max Pool Size 도달',
        },
        {
          cause: 'Connection 반환 지연',
          priority: 'Medium',
          explanation: '쿼리 실행 후 Connection 반환이 늦음',
          diagnosticCriteria: 'Average Connection Hold Time 높음',
        },
      ],
      solutions: [
        {
          title: 'Connection Pool 설정 검토',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: '적정 Pool Size 파악',
        },
        {
          title: 'Connection Pool 크기 증설',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30분-1시간',
          expectedEffect: 'Connection 부족 해소',
        },
        {
          title: 'Connection 사용 최적화 (빠른 반환)',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-5일',
          expectedEffect: 'Connection 회전율 향상',
        },
      ],
    },
  },
  database: {},
};

const ALERT_INSIGHT_EN_DATA: AlertInsightMap = {
  application: {
    XM_OUTOFMEMORYERROR: {
      type: 'event',
      description:
        'This alarm is triggered when an OutOfMemoryError occurs due to JVM heap exhaustion,\nindicating that the application cannot allocate new objects.',
      mainCauses: [
        {
          cause: 'Heap Memory Exhaustion',
          priority: 'High',
          explanation: 'Insufficient -Xmx setting',
          diagnosticCriteria: 'Heap usage ≥ 98%, -Xmx set lower than required',
        },
        {
          cause: 'Memory Leak',
          priority: 'High',
          explanation: 'Memory accumulation due to unreleased object references',
          diagnosticCriteria: 'Old Gen usage ≥ 90% after Full GC',
        },
        {
          cause: 'Large Object Allocation',
          priority: 'Medium',
          explanation: 'Large data load or large object allocation',
          diagnosticCriteria: 'OOM during batch jobs or large queries',
        },
        {
          cause: 'Metaspace Exhaustion',
          priority: 'Low',
          explanation: 'Metaspace exhaustion (Java 8+)',
          diagnosticCriteria: 'Metaspace usage reaches MaxMetaspaceSize',
        },
      ],
      solutions: [
        {
          title: 'Emergency restart and increase -Xmx',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Service recovery, reduced risk of OOM recurrence',
        },
        {
          title: 'Heap dump analysis (before OOM)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-4 hours',
          expectedEffect: 'Identify exact cause of OOM',
        },
        {
          title: 'Fix memory leak',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '3-10 days',
          expectedEffect: 'Eliminate root cause',
        },
      ],
    },
    XM_SOCKETTIMEOUTEXCEPTION: {
      type: 'event',
      description:
        'This event occurs when no network response is received within the specified time.\nPossible causes include server latency or network failures.',
      mainCauses: [
        {
          cause: 'External API response delay',
          priority: 'High',
          explanation: 'External system response exceeds timeout',
          diagnosticCriteria: 'Concentrated on specific external API calls',
        },
        {
          cause: 'Network failure',
          priority: 'High',
          explanation: 'Network degradation or outage',
          diagnosticCriteria: 'Increased network latency / packet loss',
        },
        {
          cause: 'Insufficient timeout configuration',
          priority: 'Medium',
          explanation: 'Timeout value set too low',
          diagnosticCriteria: 'Timeout shorter than normal response time',
        },
        {
          cause: 'Database response delay',
          priority: 'High',
          explanation: 'Database query execution exceeds timeout',
          diagnosticCriteria: 'Increased SQL elapsed time',
        },
      ],
      solutions: [
        {
          title: 'Identify APIs/services causing timeouts',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Identify affected services',
        },
        {
          title: 'Monitor external API response times and coordinate',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3 days',
          expectedEffect: 'Improve external system performance',
        },
        {
          title: 'Adjust timeout settings',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Prevent timeout errors for normal responses',
        },
        {
          title: 'Apply circuit breaker pattern',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3 days',
          expectedEffect: 'Prevent external failure propagation',
        },
      ],
    },
    XM_SOCKETEXCEPTION: {
      type: 'event',
      description:
        'This event occurs when an unexpected error happens on a network socket.\nPossible causes include connection termination or use of an invalid address.',
      mainCauses: [
        {
          cause: 'Connection reset',
          priority: 'High',
          explanation: 'Remote server forcibly closes the connection',
          diagnosticCriteria: 'Connection reset by peer error',
        },
        {
          cause: 'Firewall blocking',
          priority: 'High',
          explanation: 'Connection blocked by firewall policy',
          diagnosticCriteria: 'Connection attempts to a specific IP/port fail',
        },
        {
          cause: 'Invalid host/port',
          priority: 'Medium',
          explanation: 'Attempt to connect to a non-existent host or port',
          diagnosticCriteria: 'Connection refused error',
        },
        {
          cause: 'Network instability',
          priority: 'Medium',
          explanation: 'Intermittent disconnections due to network device issues',
          diagnosticCriteria: 'Occurs at specific times or intervals',
        },
      ],
      solutions: [
        {
          title: 'Analyze error logs and stack traces',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Identify exact error cause',
        },
        {
          title: 'Check target server connectivity',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Verify server availability',
        },
        {
          title: 'Review and adjust firewall policies',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-4 hours',
          expectedEffect: 'Allow communication',
        },
        {
          title: 'Implement connection retry logic',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Handle transient failures',
        },
      ],
    },
    XM_JDBC_CONNECTION_FAIL: {
      type: 'event',
      description:
        'The application failed to connect to the database.\nThis may be caused by connection pool exhaustion or database response delays.',
      mainCauses: [
        {
          cause: 'Connection pool exhaustion',
          priority: 'High',
          explanation: 'New connections blocked due to maxActive limit exceeded',
          diagnosticCriteria: 'Active connections equal to maxPoolSize',
        },
        {
          cause: 'Database server down',
          priority: 'Critical',
          explanation: 'Unable to connect due to database server failure',
          diagnosticCriteria: 'Database server ping failure / connection refused',
        },
        {
          cause: 'Connection Leak',
          priority: 'Medium',
          explanation: 'Connection pool exhausted by unreleased connections',
          diagnosticCriteria: 'Connections not returned for an extended period',
        },
        {
          cause: 'Network failure',
          priority: 'High',
          explanation: 'Network connectivity failure to database server',
          diagnosticCriteria: 'Network timeout / connection refused',
        },
      ],
      solutions: [
        {
          title: 'Check database server status',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '5-10 minutes',
          expectedEffect: 'Verify database outage',
        },
        {
          title: 'Monitor connection pool',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-20 minutes',
          expectedEffect: 'Check for pool exhaustion',
        },
        {
          title: 'Increase connection pool size',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Resolve connection shortage',
        },
        {
          title: 'Detect and fix connection leaks',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3 days',
          expectedEffect: 'Eliminate root cause of pool exhaustion',
        },
      ],
    },
    XM_TXN_PLC: {
      type: 'event',
      description:
        'This event occurs when performance degradation or wait states happen during transaction processing.\nPossible causes include resource contention or lock issues.',
      mainCauses: [
        {
          cause: 'Lock wait',
          priority: 'High',
          explanation: 'Waiting for DB or application locks',
          diagnosticCriteria: 'Thread state BLOCKED / lock-related errors',
        },
        {
          cause: 'Slow Query',
          priority: 'High',
          explanation: 'Delayed DB query execution',
          diagnosticCriteria: 'Increased SQL elapsed time',
        },
        {
          cause: 'Resource contention',
          priority: 'Medium',
          explanation: 'Contention for shared resources',
          diagnosticCriteria: 'Delays under concurrent access',
        },
      ],
      solutions: [
        {
          title: 'Thread dump analysis',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Identify lock wait points',
        },
        {
          title: 'Check DB lock status',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '20-40 minutes',
          expectedEffect: 'Determine DB lock causes',
        },
        {
          title: 'Optimize queries and minimize lock scope',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2-5 days',
          expectedEffect: 'Reduce lock wait time',
        },
      ],
    },
    XM_JDBC_TOOMANYFETCH: {
      type: 'event',
      description:
        'This alert occurs when too much data is retrieved at once,\nleading to performance degradation.\nThe recommended actions are to reduce unnecessary data retrieval or optimize queries.',
      mainCauses: [
        {
          cause: 'Paging not applied',
          priority: 'High',
          explanation: 'Retrieve entire dataset at once',
          diagnosticCriteria: 'SELECT query without LIMIT clause',
        },
        {
          cause: 'Unnecessary column selection',
          priority: 'Medium',
          explanation: 'Use of SELECT *',
          diagnosticCriteria: 'Only a subset of columns actually used',
        },
        {
          cause: 'Improper fetch size configuration',
          priority: 'Low',
          explanation: 'JDBC fetch size too large',
          diagnosticCriteria: 'Fetch Size > 1000',
        },
      ],
      solutions: [
        {
          title: 'Apply paging',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2 days',
          expectedEffect: 'Reduce memory usage and response time by 80%',
        },
        {
          title: 'Select only required columns (optimize SELECT clause)',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-3 days',
          expectedEffect: 'Reduce network traffic by 30-50%',
        },
        {
          title: 'Adjust fetch size',
          priority: 'Low',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Optimize DB-WAS round trips',
        },
      ],
    },
    app_was_cpu_usage: {
      type: 'metric',
      threshold: 'high',
      description:
        "The application's CPU usage has exceeded the threshold.\nExcessive CPU usage may cause response delays and overall system performance degradation.",
      mainCauses: [
        {
          cause: 'Infinite loop / excessive iteration',
          priority: 'High',
          explanation: 'CPU overload due to inefficient algorithms or infinite loops',
          diagnosticCriteria:
            'Single thread near 100% CPU usage / repeated stack traces in thread dump',
        },
        {
          cause: 'GC overload',
          priority: 'High',
          explanation: 'CPU consumption caused by frequent Full GC',
          diagnosticCriteria:
            'GC time > 10% of total runtime / Full GC occurs more than once per minute',
        },
        {
          cause: 'High traffic volume',
          priority: 'Medium',
          explanation: 'Thread growth and context switching due to traffic spikes',
          diagnosticCriteria: 'RUNNABLE threads exceed 2x CPU core count',
        },
        {
          cause: 'Excessive use of regular expressions',
          priority: 'Low',
          explanation: 'CPU consumption from complex regular expression matching',
          diagnosticCriteria: 'Pattern.matches() accounts for over 20% of CPU samples',
        },
      ],
      solutions: [
        {
          title: 'Identify high-CPU methods using profiling tools',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Pinpoint bottlenecks and optimize targets',
        },
        {
          title: 'Improve algorithm complexity and remove unnecessary operations',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '1-3 days',
          expectedEffect: 'Reduce CPU usage by 50-80%',
        },
        {
          title: 'Tune GC and right-size heap memory',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Improve concurrency, reduce response time by 20-40%',
        },
      ],
    },
    app_was_free_memory: {
      type: 'metric',
      threshold: 'low',
      description:
        "The application's memory usage has exceeded the threshold.\nSustained memory growth may lead to performance degradation and OOM errors.",
      mainCauses: [
        {
          cause: 'Memory leak',
          priority: 'High',
          explanation: 'Unused objects retained in heap (not GCed)',
          diagnosticCriteria: 'Old Gen usage continuously increases / remains >80% after Full GC',
        },
        {
          cause: 'Excessive cache usage',
          priority: 'Medium',
          explanation: 'Unbounded local cache growth',
          diagnosticCriteria: 'Specific cache objects occupy >30% of heap',
        },
        {
          cause: 'High traffic volume',
          priority: 'Low',
          explanation: 'Temporary memory spike due to sudden traffic increase',
          diagnosticCriteria: 'Memory spikes at specific times, reclaimed by Young GC',
        },
      ],
      solutions: [
        {
          title: 'Analyze and tune GC logs',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Improve GC efficiency, reduce pause time by 30-50%',
        },
        {
          title: 'Identify leaking objects via heap dump analysis',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-4 hours',
          expectedEffect: 'Identify root cause of memory leaks',
        },
        {
          title: 'Fix memory leak code',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2-7 days',
          expectedEffect: 'Stabilize memory usage',
        },
        {
          title: 'Increase -Xmx (temporary workaround)',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Temporarily mitigate OOM risk',
        },
      ],
    },
    app_active_transaction_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of active transactions has exceeded the threshold.\nTransaction backlog is causing response delays.',
      mainCauses: [
        {
          cause: 'Increase in slow transactions',
          priority: 'High',
          explanation: 'Long-running transactions causing backlog',
          diagnosticCriteria: 'Transaction elapsed time > 3× normal',
        },
        {
          cause: 'Database or external API latency',
          priority: 'High',
          explanation: 'Delayed transaction completion due to DB queries or external API calls',
          diagnosticCriteria: 'Increased SQL elapsed time or remote call elapsed time',
        },
        {
          cause: 'Sudden increase in concurrent users',
          priority: 'Medium',
          explanation: 'Increase in concurrent transactions due to traffic spikes',
          diagnosticCriteria: 'Active users > 2x normal / TPS spike',
        },
      ],
      solutions: [
        {
          title: 'Analyze current active transactions',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Identify bottleneck transactions',
        },
        {
          title: 'Optimize slow transaction queries',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3 days',
          expectedEffect: 'Improve transaction response time by 50-70%',
        },
        {
          title: 'Adjust thread pool size',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Increase concurrent processing capacity',
        },
      ],
    },
    app_tps: {
      type: 'metric',
      threshold: 'both',
      description:
        'Transactions per second (TPS) has deviated from the normal range.\nA sudden increase may indicate a traffic surge, while a sharp decrease may indicate a system failure.',
      mainCauses: [
        {
          cause: 'Traffic surge',
          priority: 'High',
          explanation: 'Sudden user influx due to events or marketing campaigns',
          diagnosticCriteria: 'TPS > 200% of normal',
        },
        {
          cause: 'DDoS attack',
          priority: 'Critical',
          explanation: 'Abnormal TPS increase caused by malicious high-volume requests',
          diagnosticCriteria: 'Abnormal TPS spike with increased error rate',
        },
        {
          cause: 'System failure (TPS drop)',
          priority: 'Critical',
          explanation: 'Transaction processing failure due to WAS or DB outage',
          diagnosticCriteria: 'TPS < 50% of normal with increased error rate',
        },
      ],
      solutions: [
        {
          title: 'Analyze traffic patterns',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Distinguish normal vs. abnormal traffic',
        },
        {
          title: 'Apply auto scaling',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Automatically handle traffic increases',
        },
        {
          title: 'Apply rate limiting',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '4-8 hours',
          expectedEffect: 'Block DDoS and excessive requests',
        },
      ],
    },
    app_transaction_count: {
      type: 'metric',
      threshold: 'both',
      description:
        'The total number of transactions processed per minute is abnormal.\nSudden changes may indicate system issues.',
      mainCauses: [
        {
          cause: 'Peak traffic period',
          priority: 'Low',
          explanation: 'Natural increase during regular peak hours',
          diagnosticCriteria: 'Periodic pattern / normal error rate',
        },
        {
          cause: 'Batch job execution',
          priority: 'Medium',
          explanation: 'Throughput spike due to large batch job execution',
          diagnosticCriteria: 'Concentrated at specific times / specific transaction types',
        },
        {
          cause: 'WAS instance down',
          priority: 'Critical',
          explanation: 'Overall throughput drop due to partial WAS instance failure',
          diagnosticCriteria: 'Reduced active WAS count / increased error rate',
        },
      ],
      solutions: [
        {
          title: 'Check WAS instance status',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '5-10 minutes',
          expectedEffect: 'Identify failed instances',
        },
        {
          title: 'Adjust batch job schedules',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Distribute peak load',
        },
        {
          title: 'Review load balancing policies',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-4 hours',
          expectedEffect: 'Evenly distribute traffic',
        },
      ],
    },
    app_transaction_elapse_time_avg: {
      type: 'metric',
      threshold: 'high',
      description:
        'The average transaction response time has exceeded the threshold.\nThis may degrade user experience and indicate performance issues.',
      mainCauses: [
        {
          cause: 'Degraded DB query performance',
          priority: 'High',
          explanation: 'Overall response time increase due to more slow queries',
          diagnosticCriteria: 'Increased average SQL elapsed time',
        },
        {
          cause: 'External API response delay',
          priority: 'High',
          explanation: 'Delayed responses from integrated external systems',
          diagnosticCriteria: 'Increased remote call elapsed time',
        },
        {
          cause: 'GC overload',
          priority: 'Medium',
          explanation: 'Application response delays caused by frequent GC',
          diagnosticCriteria: 'Increased GC time / high heap usage',
        },
      ],
      solutions: [
        {
          title: 'Identify bottlenecks via APM trace analysis',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Accurately identify response time delays',
        },
        {
          title: 'Optimize slow queries',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '1-5 days',
          expectedEffect: 'Improve response time by 40-60%',
        },
        {
          title: 'Apply caching',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-3 days',
          expectedEffect: 'Reduce DB load, improve response time by 30-50%',
        },
      ],
    },
    app_transaction_elapse_time: {
      type: 'metric',
      threshold: 'high',
      description:
        'The total transaction response time has increased abnormally.\nThis indicates overall system performance degradation.',
      mainCauses: [
        {
          cause: 'Increased transaction throughput',
          priority: 'Medium',
          explanation: 'Increase due to normal traffic growth',
          diagnosticCriteria: 'TPS increase with normal average response time',
        },
        {
          cause: 'Delayed individual transactions',
          priority: 'High',
          explanation: 'Increased response time per transaction',
          diagnosticCriteria: 'Average response time also increasing',
        },
        {
          cause: 'Backlog in specific transactions',
          priority: 'High',
          explanation: 'A few slow transactions inflate the total',
          diagnosticCriteria: 'Extremely high response time for specific URL patterns',
        },
      ],
      solutions: [
        {
          title: 'Analyze response time by transaction',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '20-40 minutes',
          expectedEffect: 'Identify problematic transactions',
        },
        {
          title: 'Optimize slow transactions',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5 days',
          expectedEffect: 'Reduce total response time by 30-50%',
        },
      ],
    },
    app_transaction_exception_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'Transaction error count has exceeded the threshold.\nThis may indicate business logic errors or system failures.',
      mainCauses: [
        {
          cause: 'Database connection failure',
          priority: 'Critical',
          explanation: 'Connection pool exhaustion or DB server failure',
          diagnosticCriteria: 'Multiple JDBC exceptions',
        },
        {
          cause: 'External API failure',
          priority: 'High',
          explanation: 'Errors caused by external system failure',
          diagnosticCriteria: 'Increase in timeout exceptions / connection refused errors',
        },
      ],
      solutions: [
        {
          title: 'Analyze error logs and stack traces',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Identify error cause',
        },
        {
          title: 'Emergency rollback (after deployment)',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Restore service',
        },
        {
          title: 'Fix bugs and redeploy',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: 'Several hours-several days',
          expectedEffect: 'Resolve root cause',
        },
      ],
    },
    app_txn_error_ratio: {
      type: 'metric',
      threshold: 'high',
      description:
        'The error rate has exceeded the threshold relative to total transactions.\nService quality is degrading.',
      mainCauses: [
        {
          cause: 'Insufficient system resources',
          priority: 'High',
          explanation: 'Request failures due to insufficient CPU, memory, or threads',
          diagnosticCriteria: 'CPU Usage > 90%, Memory shortage',
        },
        {
          cause: 'Dependency service failure',
          priority: 'High',
          explanation: 'Failures in dependent services (DB, cache, external APIs)',
          diagnosticCriteria: 'Concentrated errors during specific service calls',
        },
        {
          cause: 'Code quality issues',
          priority: 'Medium',
          explanation: 'Inadequate exception handling / unhandled edge cases',
          diagnosticCriteria: 'Various Exception types occur',
        },
      ],
      solutions: [
        {
          title: 'Analyze error patterns',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Identify common root causes',
        },
        {
          title: 'Scale up resources',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-4 hours',
          expectedEffect: 'Increase system capacity',
        },
      ],
    },
    app_txn_error_count: {
      type: 'metric',
      threshold: 'high',
      description: 'Error transaction count has exceeded the threshold.',
      mainCauses: [
        {
          cause: 'Overall traffic increase',
          priority: 'High',
          explanation: 'Error increase proportional to traffic growth',
          diagnosticCriteria: 'TPS increase with normal error rate',
        },
        {
          cause: 'Failure in specific functionality',
          priority: 'Medium',
          explanation: 'Persistent errors in specific features',
          diagnosticCriteria: 'Concentrated on specific API endpoints',
        },
      ],
      solutions: [
        {
          title: 'Analyze error-prone API endpoints',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '15-30 minutes',
          expectedEffect: 'Identify affected functionality',
        },
        {
          title: 'Apply emergency patch to the affected feature',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1-8 hours',
          expectedEffect: 'Stop error occurrences',
        },
      ],
    },
    app_remote_elapse: {
      type: 'metric',
      threshold: 'high',
      description:
        'The total remote call response time has increased.\nThis indicates degraded performance in external system integrations.',
      mainCauses: [
        {
          cause: 'External API response delay',
          priority: 'High',
          explanation: 'Degraded performance of integrated external systems',
          diagnosticCriteria: 'Increased average remote call response time',
        },
        {
          cause: 'Increase in remote call frequency',
          priority: 'Medium',
          explanation: 'Increase in external calls due to business logic changes',
          diagnosticCriteria: 'Remote call count increase',
        },
        {
          cause: 'Network latency',
          priority: 'Medium',
          explanation: 'Delays caused by network degradation',
          diagnosticCriteria: 'Increased network latency',
        },
      ],
      solutions: [
        {
          title: 'Monitor external API response times',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-20 minutes',
          expectedEffect: 'Identify delayed external systems',
        },
        {
          title: 'Adjust timeout settings and apply circuit breaker',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Prevent failure propagation',
        },
        {
          title: 'Apply caching (if applicable)',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3 days',
          expectedEffect: 'Reduce external call frequency',
        },
      ],
    },
    app_sql_exec_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of SQL executions has exceeded the threshold.\nThis may increase database load and degrade performance.',
      mainCauses: [
        {
          cause: 'N+1 query issue',
          priority: 'High',
          explanation: 'Query explosion due to per-iteration execution in loops',
          diagnosticCriteria: 'Multiple executions of the same query in a single transaction',
        },
        {
          cause: 'Traffic increase',
          priority: 'Medium',
          explanation: 'Query increase due to normal traffic growth',
          diagnosticCriteria: 'Proportional to TPS increase',
        },
        {
          cause: 'Inefficient data access',
          priority: 'Medium',
          explanation: 'Repeated retrieval of unnecessary data',
          diagnosticCriteria: 'Repeated access to the same data',
        },
      ],
      solutions: [
        {
          title: 'Analyze SQL execution patterns',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Identify N+1 queries',
        },
        {
          title: 'Optimize queries (JOINs, batch queries)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-3 days',
          expectedEffect: 'Reduce SQL executions by 70-90%',
        },
        {
          title: 'Apply caching',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3 days',
          expectedEffect: 'Reduce DB load by 30-50%',
        },
      ],
    },
    app_sql_prepare_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of SQL prepares is excessively high.\nThis indicates insufficient PreparedStatement reuse or query creation issues.',
      mainCauses: [
        {
          cause: 'PreparedStatement not reused',
          priority: 'High',
          explanation: 'Create a new PreparedStatement every time',
          diagnosticCriteria: 'Prepare-to-execute ratio close to 1:1',
        },
        {
          cause: 'Improper connection pool configuration',
          priority: 'Medium',
          explanation: 'Frequent connection creation and closure',
          diagnosticCriteria: 'Low connection reuse rate',
        },
      ],
      solutions: [
        {
          title: 'Improve PreparedStatement reuse structure',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5 days',
          expectedEffect: 'Reduce prepare count by 50-70%',
        },
        {
          title: 'Optimize connection pool configuration',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Improve connection reuse rate',
        },
      ],
    },
    app_sql_fetch_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of SQL fetches is extremely high.\nRetrieving large volumes of data may cause performance degradation and excessive memory usage.',
      mainCauses: [
        {
          cause: 'Paging not applied',
          priority: 'High',
          explanation: 'Retrieve entire dataset at once',
          diagnosticCriteria: 'Query without LIMIT clause / very large ResultSet',
        },
        {
          cause: 'Unnecessary column selection',
          priority: 'Medium',
          explanation: 'Use of SELECT *',
          diagnosticCriteria: 'Only a subset of columns actually used',
        },
        {
          cause: 'Insufficient batch processing',
          priority: 'Medium',
          explanation: 'Batch processing not applied for large datasets',
          diagnosticCriteria: 'Row-by-row fetching',
        },
      ],
      solutions: [
        {
          title: 'Apply paging',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2 days',
          expectedEffect: 'Reduce fetch count and memory usage by 80%',
        },
        {
          title: 'Select only required columns (optimize SELECT clause)',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-3 days',
          expectedEffect: 'Reduce network traffic and memory usage by 30-50%',
        },
        {
          title: 'Adjust fetch size',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Reduce DB-WAS round trips',
        },
      ],
    },
    app_sql_elapse_time_avg: {
      type: 'metric',
      threshold: 'high',
      description:
        'The average SQL execution time is delayed.\nQuery optimization or database resource checks are required.',
      mainCauses: [
        {
          cause: 'Missing indexes',
          priority: 'High',
          explanation: 'Full table scan due to missing index on WHERE clause',
          diagnosticCriteria: 'Table scan shown in execution plan',
        },
        {
          cause: 'Complex JOINS',
          priority: 'High',
          explanation: 'Increased query complexity due to multiple JOINS',
          diagnosticCriteria: 'More than 3 JOINs on large tables',
        },
        {
          cause: 'Insufficient DB resources',
          priority: 'Medium',
          explanation: 'Insufficient DB CPU, memory, or disk I/O',
          diagnosticCriteria: 'DB server resource usage > 80%',
        },
      ],
      solutions: [
        {
          title: 'Analyze slow queries and review execution plans',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Identify problematic queries',
        },
        {
          title: 'Select only required columns (optimize SELECT clause)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Improve query execution time by 70-90%',
        },
        {
          title: 'Refactor queries (minimize JOINs, remove subqueries)',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '3-7 days',
          expectedEffect: 'Improve query performance by 50-80%',
        },
      ],
    },
    app_sql_elapse_time: {
      type: 'metric',
      threshold: 'high',
      description:
        'The total SQL execution time has increased.\nThis indicates an overall increase in database load.',
      mainCauses: [
        {
          cause: 'Increase in SQL execution count',
          priority: 'Medium',
          explanation: 'Increased query frequency due to traffic growth',
          diagnosticCriteria: 'SQL execute count increase',
        },
        {
          cause: 'Degraded individual query performance',
          priority: 'High',
          explanation: 'Increased execution time per query',
          diagnosticCriteria: 'Increased average SQL elapsed time',
        },
      ],
      solutions: [
        {
          title: 'Analyze SQL execution patterns and performance',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Identify root cause (frequency vs. performance)',
        },
        {
          title: 'Optimize queries',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5 days',
          expectedEffect: 'Reduce total SQL time by 40-60%',
        },
      ],
    },
    app_connection_time: {
      type: 'metric',
      threshold: 'high',
      description:
        'The total database connection time has increased.\nDelays are occurring while acquiring connections.',
      mainCauses: [
        {
          cause: 'Insufficient connection pool',
          priority: 'High',
          explanation: 'Increased wait time to acquire connections from the pool',
          diagnosticCriteria: 'Active connections near max pool size',
        },
        {
          cause: 'Database network latency',
          priority: 'Medium',
          explanation: 'Network latency to DB server',
          diagnosticCriteria: 'Increased network latency',
        },
        {
          cause: 'Connection Leak',
          priority: 'High',
          explanation: 'Pool exhaustion due to unreleased connections',
          diagnosticCriteria: 'Connections not returned after use',
        },
      ],
      solutions: [
        {
          title: 'Monitor connection pool',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-20 minutes',
          expectedEffect: 'Assess pool status',
        },
        {
          title: 'Increase connection pool size',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Reduce connection wait time',
        },
        {
          title: 'Detect and fix connection leaks',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1-3 days',
          expectedEffect: 'Prevent pool exhaustion',
        },
      ],
    },
    app_http_call: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of remote calls has increased.\nDependency on external systems is growing.',
      mainCauses: [
        {
          cause: 'Business logic changes',
          priority: 'Medium',
          explanation: 'External API calls increased due to new features',
          diagnosticCriteria: 'Occurred after deploying a specific feature',
        },
        {
          cause: 'N+1 remote call issue',
          priority: 'High',
          explanation: 'External API calls inside loops',
          diagnosticCriteria: 'Repeated calls to the same API',
        },
        {
          cause: 'Traffic increase',
          priority: 'Low',
          explanation: 'Proportional to normal traffic growth',
          diagnosticCriteria: 'Proportional to TPS increase',
        },
      ],
      solutions: [
        {
          title: 'Analyze remote call patterns',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Identify N+1 issues',
        },
        {
          title: 'Switch to batch API calls',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-4 days',
          expectedEffect: 'Reduce remote calls by 70-90%',
        },
        {
          title: 'Apply caching',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-3 days',
          expectedEffect: 'Reduce external calls by 30-50%',
        },
      ],
    },
    app_active_user: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of active users has surged.\nThis may indicate a traffic spike or event-driven influx.',
      mainCauses: [
        {
          cause: 'Marketing campaign',
          priority: 'Low',
          explanation: 'Natural increase due to planned events',
          diagnosticCriteria: 'Pre-announced event time window',
        },
        {
          cause: 'Media exposure',
          priority: 'Medium',
          explanation: 'Sudden influx via news or social media',
          diagnosticCriteria: 'Unexpected traffic surge',
        },
        {
          cause: 'Bot attack',
          priority: 'High',
          explanation: 'Abnormal access by automated bots',
          diagnosticCriteria: 'Abnormal access patterns',
        },
      ],
      solutions: [
        {
          title: 'Analyze traffic sources and patterns',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Differentiate normal vs. abnormal traffic',
        },
        {
          title: 'Enable auto scaling',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: 'Immediate',
          expectedEffect: 'Automatically scale system capacity',
        },
        {
          title: 'Block bots (if abnormal)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-4 hours',
          expectedEffect: 'Block abnormal traffic',
        },
      ],
    },
    app_concurrent_user: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of concurrent users has surged.\nThis may lead to insufficient system resources.',
      mainCauses: [
        {
          cause: 'Peak hours',
          priority: 'Low',
          explanation: 'Regular peak hours (lunch, evening, etc.)',
          diagnosticCriteria: 'Periodic pattern',
        },
        {
          cause: 'Event start',
          priority: 'Medium',
          explanation: 'Concurrent spikes from limited sales or ticket releases',
          diagnosticCriteria: 'Concentrated at specific times',
        },
        {
          cause: 'Excessive session timeout configuration',
          priority: 'Medium',
          explanation: 'Sessions retained too long and accumulated',
          diagnosticCriteria: 'Session timeout set too high',
        },
      ],
      solutions: [
        {
          title: 'Review session timeout settings',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Clean up unnecessary sessions',
        },
        {
          title: 'Scale session storage (e.g., Redis)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Increase session handling capacity',
        },
      ],
    },
    app_apdex: {
      type: 'metric',
      threshold: 'low',
      description:
        'The Apdex (Application Performance Index) score has degraded.\nUser experience is deteriorating.',
      mainCauses: [
        {
          cause: 'Increased response time',
          priority: 'High',
          explanation: 'Decreased satisfaction due to increased transaction elapsed time',
          diagnosticCriteria: 'Average response time > 3 seconds',
        },
        {
          cause: 'Increased error rate',
          priority: 'High',
          explanation: 'User dissatisfaction caused by errors',
          diagnosticCriteria: 'Error transaction ratio > 1%',
        },
        {
          cause: 'Increased timeouts',
          priority: 'Medium',
          explanation: 'Requests not completed due to timeouts',
          diagnosticCriteria: 'Increase in timeout exceptions',
        },
      ],
      solutions: [
        {
          title: 'Analyze Apdex bad-count transactions',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Identify dissatisfaction causes',
        },
        {
          title: 'Improve response time (query optimization, caching, etc.)',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5 days',
          expectedEffect: 'Improve Apdex score',
        },
      ],
    },
    app_apdex_excellent: {
      type: 'metric',
      threshold: 'low',
      description:
        'The number of excellent-response transactions has decreased.\nFewer transactions are delivering fast responses.',
      mainCauses: [
        {
          cause: 'Overall performance degradation',
          priority: 'High',
          explanation: 'Overall system response time slowdown',
          diagnosticCriteria: 'Increased average response time',
        },
        {
          cause: 'Traffic increase',
          priority: 'Medium',
          explanation: 'Response time increase due to higher load',
          diagnosticCriteria: 'TPS increase',
        },
      ],
      solutions: [
        {
          title: 'Analyze performance degradation causes',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1-2 hours',
          expectedEffect: 'Identify bottlenecks',
        },
        {
          title: 'Optimize system',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '3-7 days',
          expectedEffect: 'Increase excellent transaction count',
        },
      ],
    },
    app_apdex_warning: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of warning-level response transactions has increased.\nResponse times are exceeding the target.',
      mainCauses: [
        {
          cause: 'Delayed transactions',
          priority: 'High',
          explanation: 'Specific transactions slowed down',
          diagnosticCriteria: 'Delays on specific URL patterns',
        },
        {
          cause: 'Insufficient system resources',
          priority: 'Medium',
          explanation: 'Response delays due to CPU or memory shortages',
          diagnosticCriteria: 'Resource utilization > 80%',
        },
      ],
      solutions: [
        {
          title: 'Analyze warning-level transactions',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Identify delayed transactions',
        },
        {
          title: 'Optimize affected transactions',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2-5 days',
          expectedEffect: 'Reduce warning count',
        },
      ],
    },
    app_apdex_bad: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of bad-response transactions has increased.\nSevere response delays are occurring.',
      mainCauses: [
        {
          cause: 'Severe performance issues',
          priority: 'Critical',
          explanation: 'Response delays at system-failure level',
          diagnosticCriteria: 'Response time > 12 seconds (4x Apdex T)',
        },
        {
          cause: 'Failure in specific functionality',
          priority: 'High',
          explanation: 'Severe delays in specific features',
          diagnosticCriteria: 'Concentrated on specific URLs',
        },
      ],
      solutions: [
        {
          title: 'Urgent analysis of bad-count transactions',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Identify items requiring immediate action',
        },
        {
          title: 'Take emergency actions (consider temporarily disabling the affected feature)',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: 'Immediate',
          expectedEffect: 'Protect overall system',
        },
      ],
    },
    app_was_memory_total: {
      type: 'metric',
      threshold: 'high',
      description: "The application's overall memory usage is excessively high.",
      mainCauses: [
        {
          cause: 'Memory leak',
          priority: 'High',
          explanation: 'Unused objects retained in memory',
          diagnosticCriteria: 'Continuous increase in memory usage',
        },
        {
          cause: 'Large-scale data processing',
          priority: 'Medium',
          explanation: 'Loading large amounts of data into memory at once',
          diagnosticCriteria: 'Memory spikes during specific operations',
        },
      ],
      solutions: [
        {
          title: 'Heap dump analysis',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1–3 hours',
          expectedEffect: 'Identify memory-consuming objects',
        },
        {
          title: 'Fix memory leaks',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2–7 days',
          expectedEffect: 'Stabilize memory usage',
        },
      ],
    },
    app_was_memory_rss: {
      type: 'metric',
      threshold: 'high',
      description:
        "The application's RSS (Resident Set Size) memory usage is excessively high.\nActual physical memory usage is elevated.",
      mainCauses: [
        {
          cause: 'Increased non-heap memory usage',
          priority: 'High',
          explanation: 'Usage of native memory and direct buffers',
          diagnosticCriteria: 'Normal heap usage but high RSS',
        },
        {
          cause: 'JVM off-heap memory usage',
          priority: 'Medium',
          explanation: 'Increase in Metaspace and code cache',
          diagnosticCriteria: 'Metaspace usage increase',
        },
      ],
      solutions: [
        {
          title: 'Enable Native Memory Tracking',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Identify off-heap memory usage',
        },
        {
          title: 'Review direct buffer usage',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-4 days',
          expectedEffect: 'Optimize native memory usage',
        },
      ],
    },
    app_pod_cpu_usage: {
      type: 'metric',
      threshold: 'high',
      description:
        "The Kubernetes pod's CPU usage is excessively high.\nThe pod may reach its CPU resource limit.",
      mainCauses: [
        {
          cause: 'Insufficient CPU limit configuration',
          priority: 'High',
          explanation: 'Insufficient CPU allocated to the pod',
          diagnosticCriteria: 'CPU requests/limits set lower than required',
        },
        {
          cause: 'Excessive CPU usage by the application',
          priority: 'High',
          explanation: 'Application-level CPU issues',
          diagnosticCriteria: 'High application CPU usage',
        },
      ],
      solutions: [
        {
          title: 'Increase pod CPU limit',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Prevent CPU throttling',
        },
        {
          title: 'Configure HPA (Horizontal Pod Autoscaler)',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1-2 days',
          expectedEffect: 'Enable load-based auto scaling',
        },
      ],
    },
    app_pod_memory_usage: {
      type: 'metric',
      threshold: 'high',
      description:
        "The Kubernetes pod's memory usage is excessively high.\nThere is a risk of OOMKilled.",
      mainCauses: [
        {
          cause: 'Insufficient memory limit configuration',
          priority: 'High',
          explanation: 'Insufficient memory allocated to the pod',
          diagnosticCriteria: 'Memory limit set lower than required',
        },
        {
          cause: 'Application memory leak',
          priority: 'High',
          explanation: 'Application memory leak',
          diagnosticCriteria: 'High application memory usage',
        },
      ],
      solutions: [
        {
          title: 'Increase pod memory limit',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Prevent OOMKilled',
        },
        {
          title: 'Optimize application memory usage',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2-7 days',
          expectedEffect: 'Reduce memory usage at the root cause',
        },
      ],
    },
    app_db_active_connection_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'The number of active DB connections is excessively high.\nThere is a risk of connection pool exhaustion.',
      mainCauses: [
        {
          cause: 'Long Running Query',
          priority: 'High',
          explanation: 'Increased connection hold time due to long-running queries',
          diagnosticCriteria: 'High SQL elapsed time',
        },
        {
          cause: 'Connection Leak',
          priority: 'Critical',
          explanation: 'Connections not returned after use',
          diagnosticCriteria: 'Continuous increase in active connections',
        },
        {
          cause: 'Traffic increase',
          priority: 'Medium',
          explanation: 'Higher DB connection demand due to increased requests',
          diagnosticCriteria: 'TPS increase',
        },
      ],
      solutions: [
        {
          title: 'Analyze current active connections',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10-30 minutes',
          expectedEffect: 'Identify long-running queries',
        },
        {
          title: 'Check and fix connection leaks',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1-3 days',
          expectedEffect: 'Stabilize connection pool',
        },
        {
          title: 'Increase connection pool size',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Ensure pool capacity',
        },
      ],
    },
    app_db_total_connection_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'The total number of DB connections is approaching the pool size.\nThere is a risk that new connections cannot be created.',
      mainCauses: [
        {
          cause: 'Insufficient connection pool size',
          priority: 'High',
          explanation: 'Pool size smaller than actual demand',
          diagnosticCriteria: 'Frequently reaching max pool size',
        },
        {
          cause: 'Delayed connection release',
          priority: 'Medium',
          explanation: 'Delayed connection release after query execution',
          diagnosticCriteria: 'High average connection hold time',
        },
      ],
      solutions: [
        {
          title: 'Review connection pool configuration',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Determine optimal pool size',
        },
        {
          title: 'Increase connection pool size',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30 minutes-1 hour',
          expectedEffect: 'Resolve connection shortage',
        },
        {
          title: 'Optimize connection usage (faster release)',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2-5 days',
          expectedEffect: 'Improve connection turnover rate',
        },
      ],
    },
  },
  database: {},
};

const ALERT_INSIGHT_JA_DATA: AlertInsightMap = {
  application: {
    XM_OUTOFMEMORYERROR: {
      type: 'event',
      description:
        'アプリケーションでメモリ不足（OutOfMemoryError）イベントが検出されました。\nJVM Heapが限界に達し、新しいオブジェクトを割り当てできない深刻な状態です。',
      mainCauses: [
        {
          cause: 'Heapメモリ不足',
          priority: 'High',
          explanation: 'JVM最大Heapサイズ（-Xmx）の設定不足',
          diagnosticCriteria: 'Heap使用率98%以上、-Xmx設定が実際の必要量より小さい',
        },
        {
          cause: 'メモリリーク',
          priority: 'High',
          explanation: '使用されていないオブジェクトが参照されたままGCされずに蓄積',
          diagnosticCriteria: 'Full GC後もOld Generation使用率が90%以上',
        },
        {
          cause: '大容量オブジェクト生成',
          priority: 'Medium',
          explanation: '一度に大量のデータをロードしたり、大きなオブジェクトを生成',
          diagnosticCriteria: '特定のバッチ処理または大量検索時にOOMが発生',
        },
        {
          cause: 'Metaspace不足',
          priority: 'Low',
          explanation: 'クラスメタデータ領域の不足（Java 8以上）',
          diagnosticCriteria: 'Metaspace使用量がMaxMetaspaceSizeに到達',
        },
      ],
      solutions: [
        {
          title: '緊急再起動および-Xmx増設',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: 'サービスの正常化、OOM再発リスクの低減',
        },
        {
          title: 'Heap Dump分析（OOM発生直前）',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜4時間',
          expectedEffect: 'OOMの原因を正確に特定',
        },
        {
          title: 'メモリリーク修正',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '3〜10日',
          expectedEffect: '根本原因の除去',
        },
      ],
    },
    XM_SOCKETTIMEOUTEXCEPTION: {
      type: 'event',
      description:
        '指定された時間内にネットワーク応答がない場合に発生するイベントです。\nサーバー遅延、ネットワーク障害などが原因として考えられます。',
      mainCauses: [
        {
          cause: '外部APIの応答遅延',
          priority: 'High',
          explanation: '連携先の外部システムの応答がTimeout設定より遅い',
          diagnosticCriteria: '特定の外部API呼び出し時に集中発生',
        },
        {
          cause: 'ネットワーク障害',
          priority: 'High',
          explanation: 'ネットワーク品質の低下または障害',
          diagnosticCriteria: 'ネットワークLatencyの増加、Packet Lossの発生',
        },
        {
          cause: 'Timeout設定の不足',
          priority: 'Medium',
          explanation: 'Timeout値が短すぎる設定',
          diagnosticCriteria: '正常応答時間よりTimeoutが短い',
        },
        {
          cause: 'DB応答遅延',
          priority: 'High',
          explanation: 'DBクエリの実行時間がTimeoutを超過',
          diagnosticCriteria: 'SQL Elapse Timeの増加',
        },
      ],
      solutions: [
        {
          title: 'Timeout発生API/サービスの特定',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: '問題のあるサービスの特定',
        },
        {
          title: '外部API応答時間のモニタリングおよび協議',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜3日',
          expectedEffect: '外部システムのパフォーマンス改善',
        },
        {
          title: 'Timeout設定の調整',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: '正常応答に対するTimeoutエラーの防止',
        },
        {
          title: 'Circuit Breakerパターンの適用',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2〜3日',
          expectedEffect: '外部障害の伝播防止',
        },
      ],
    },
    XM_SOCKETEXCEPTION: {
      type: 'event',
      description:
        'ネットワークソケットで予期しないエラーが発生した場合のイベントです。\n接続切断、不正なアドレスの使用などが原因として考えられます。',
      mainCauses: [
        {
          cause: '接続切断',
          priority: 'High',
          explanation: '相手先サーバーが接続を強制的に切断',
          diagnosticCriteria: 'Connection reset by peerエラーメッセージ',
        },
        {
          cause: 'ファイアウォールによるブロック',
          priority: 'High',
          explanation: 'ファイアウォールポリシーによる接続ブロック',
          diagnosticCriteria: '特定のIP/ポートへの接続試行失敗',
        },
        {
          cause: '不正なホスト/ポート',
          priority: 'Medium',
          explanation: '存在しないホストまたはポートへの接続試行',
          diagnosticCriteria: 'Connection refusedエラー',
        },
        {
          cause: 'ネットワーク不安定',
          priority: 'Medium',
          explanation: 'ネットワーク機器の問題による断続的な接続切断',
          diagnosticCriteria: '特定の時間帯または特定の区間で発生',
        },
      ],
      solutions: [
        {
          title: 'エラーログおよびスタックトレースの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: '正確なエラー原因の特定',
        },
        {
          title: '対象サーバーの接続状態確認',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: 'サーバー障害の有無を確認',
        },
        {
          title: 'ファイアウォールポリシーの確認および調整',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜4時間',
          expectedEffect: '通信の許可',
        },
        {
          title: '接続リトライロジックの実装',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: '一時的な障害への対応',
        },
      ],
    },
    XM_JDBC_CONNECTION_FAIL: {
      type: 'event',
      description:
        'アプリケーションのデータベース接続が失敗しました。\nConnection Poolが枯渇したか、DBの応答遅延が発生しています。',
      mainCauses: [
        {
          cause: 'Connection Poolの飽和',
          priority: 'High',
          explanation: 'maxActive設定値の超過により新規接続が不可',
          diagnosticCriteria: 'Active ConnectionsがmaxPoolSizeと同一',
        },
        {
          cause: 'DBサーバーダウン',
          priority: 'Critical',
          explanation: 'DBサーバー障害による接続不可',
          diagnosticCriteria: 'DBサーバーのping失敗、接続拒否',
        },
        {
          cause: 'Connection Leak',
          priority: 'Medium',
          explanation: '使用済みConnectionが返却されずPoolが枯渇',
          diagnosticCriteria: 'Connectionが一定時間以上返却されない',
        },
        {
          cause: 'ネットワーク障害',
          priority: 'High',
          explanation: 'DBサーバーとのネットワーク接続不可',
          diagnosticCriteria: 'ネットワークTimeout、Connection Refused',
        },
      ],
      solutions: [
        {
          title: 'DBサーバーの状態確認',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '5〜10分',
          expectedEffect: 'DB障害の有無を確認',
        },
        {
          title: 'Connection Poolモニタリング',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜20分',
          expectedEffect: 'Pool枯渇の有無を確認',
        },
        {
          title: 'Connection Poolサイズの増設',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'Connection不足の解消',
        },
        {
          title: 'Connection Leakの検出および修正',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜3日',
          expectedEffect: 'Pool枯渇の根本原因を解決',
        },
      ],
    },
    XM_TXN_PLC: {
      type: 'event',
      description:
        'トランザクション処理中にパフォーマンス低下または待機状態が発生するケースです。\nリソース競合やロック問題などが原因として考えられます。',
      mainCauses: [
        {
          cause: 'Lock待ち',
          priority: 'High',
          explanation: 'DB LockまたはアプリケーションLockの待機',
          diagnosticCriteria: 'Thread StateがBLOCKED、Lock関連エラー',
        },
        {
          cause: 'Slow Query',
          priority: 'High',
          explanation: 'DBクエリの実行時間遅延',
          diagnosticCriteria: 'SQL Elapse Timeの増加',
        },
        {
          cause: 'リソース競合',
          priority: 'Medium',
          explanation: '共有リソースへのアクセス競合',
          diagnosticCriteria: '同時アクセス時に遅延が発生',
        },
      ],
      solutions: [
        {
          title: 'Thread Dump分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'Lock待ち区間の特定',
        },
        {
          title: 'DB Lock状態の確認',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '20〜40分',
          expectedEffect: 'DB Lockの原因を特定',
        },
        {
          title: 'クエリ最適化およびLock範囲の最小化',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2〜5日',
          expectedEffect: 'Lock待ち時間の短縮',
        },
      ],
    },
    XM_JDBC_TOOMANYFETCH: {
      type: 'event',
      description:
        '一度に取得するデータ量が多すぎてパフォーマンス低下が発生するケースです。\n不要なデータ取得を減らすか、クエリを最適化する必要があります。',
      mainCauses: [
        {
          cause: 'ページング未適用',
          priority: 'High',
          explanation: '全データを一括取得',
          diagnosticCriteria: 'LIMIT句のないSELECTクエリ',
        },
        {
          cause: '不要なカラムの取得',
          priority: 'Medium',
          explanation: 'SELECT *の使用により全カラムを取得',
          diagnosticCriteria: '実際に使用するカラムは一部のみ',
        },
        {
          cause: 'Fetch Size設定の不適切',
          priority: 'Low',
          explanation: 'JDBC Fetch Sizeが大きすぎる',
          diagnosticCriteria: 'Fetch Size > 1000',
        },
      ],
      solutions: [
        {
          title: 'ページングの適用',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1〜2日',
          expectedEffect: 'メモリ使用量および応答時間の80%削減',
        },
        {
          title: '必要なカラムのみ取得（SELECT句の最適化）',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1〜3日',
          expectedEffect: 'ネットワークトラフィックの30〜50%削減',
        },
        {
          title: 'Fetch Sizeの調整',
          priority: 'Low',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: 'DB-WAS間のRound Trip最適化',
        },
      ],
    },
    app_was_cpu_usage: {
      type: 'metric',
      threshold: 'high',
      description:
        'アプリケーションのCPU使用率がしきい値を超過しました。\n過度なCPU使用は応答遅延およびシステム全体のパフォーマンス低下を引き起こす可能性があります。',
      mainCauses: [
        {
          cause: '無限ループまたは過度な繰り返し',
          priority: 'High',
          explanation: '非効率なアルゴリズムや無限ループによるCPU過負荷',
          diagnosticCriteria:
            '特定スレッドのCPU使用率が100%近く、Thread Dumpで同一スタックが繰り返し出現',
        },
        {
          cause: 'GC過負荷',
          priority: 'High',
          explanation: '頻繁なFull GCによるCPUリソースの消費',
          diagnosticCriteria: 'GC Timeが全実行時間の10%以上、Full GCが1分に1回以上発生',
        },
        {
          cause: '大量トラフィック処理',
          priority: 'Medium',
          explanation: '同時リクエストの急増によるThread増加およびContext Switching',
          diagnosticCriteria: 'RUNNABLE状態のThreadがCPUコア数の2倍以上',
        },
        {
          cause: '正規表現の過剰使用',
          priority: 'Low',
          explanation: '複雑な正規表現パターンマッチングによるCPU消費',
          diagnosticCriteria: 'Pattern.matches()メソッドがCPUサンプルの20%以上を占有',
        },
      ],
      solutions: [
        {
          title: 'プロファイリングツールによるCPU使用率の高いメソッドの特定',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜2時間',
          expectedEffect: 'ボトルネックの正確な把握およびターゲット最適化',
        },
        {
          title: 'アルゴリズムの複雑度改善および不要な演算の除去',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '1〜3日',
          expectedEffect: 'CPU使用率の50〜80%削減',
        },
        {
          title: 'GCチューニングおよびHeapメモリサイズの適正化',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: '並行性の改善、応答時間の20〜40%短縮',
        },
      ],
    },
    app_was_free_memory: {
      type: 'metric',
      threshold: 'low',
      description:
        'アプリケーションのメモリ使用率がしきい値を超過しました。\n継続的なメモリ増加はパフォーマンス低下およびOOMにつながる可能性があります。',
      mainCauses: [
        {
          cause: 'メモリリーク',
          priority: 'High',
          explanation: '未使用オブジェクトがGCされずHeapに残留している',
          diagnosticCriteria:
            'Old Generation使用量が継続的に増加し、Full GC後もメモリ使用率が80%以上を維持',
        },
        {
          cause: 'キャッシュの過剰使用',
          priority: 'Medium',
          explanation: 'Local Cacheのサイズ制限なく無制限に増加',
          diagnosticCriteria: '特定のキャッシュオブジェクトがHeapの30%以上を占有',
        },
        {
          cause: '大量トラフィックの流入',
          priority: 'Low',
          explanation: '急激なリクエスト増加による一時的なメモリ急増',
          diagnosticCriteria: '特定の時間帯のみメモリが急増し、Young GCで回収される',
        },
      ],
      solutions: [
        {
          title: 'GCログ分析およびチューニング',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜2時間',
          expectedEffect: 'GC効率の向上、Pause Time 30〜50%削減',
        },
        {
          title: 'Heap Dump分析によるメモリリークオブジェクトの特定',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜4時間',
          expectedEffect: 'メモリリークの原因特定',
        },
        {
          title: 'メモリリークコードの修正',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2〜7日',
          expectedEffect: 'メモリ使用量の安定化',
        },
        {
          title: '-Xmxサイズの増設（一時的な対処）',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'OOMリスクの一時的な回避',
        },
      ],
    },
    app_active_transaction_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'アクティブトランザクションが閾値を超過しました。\nトランザクションの滞留により応答遅延が発生しています。',
      mainCauses: [
        {
          cause: 'Slow Transactionの増加',
          priority: 'High',
          explanation: '特定トランザクションの応答時間が長くなり、完了せず滞留',
          diagnosticCriteria: 'Transaction Elapse Timeが通常の3倍以上',
        },
        {
          cause: 'DBまたは外部APIの遅延',
          priority: 'High',
          explanation: 'DBクエリまたは外部API呼び出しの遅延によるトランザクション完了の遅延',
          diagnosticCriteria: 'SQL Elapse TimeまたはRemote Call Elapse Timeの増加',
        },
        {
          cause: '同時ユーザーの急増',
          priority: 'Medium',
          explanation: '急激なトラフィック増加による同時処理トランザクション数の増加',
          diagnosticCriteria: 'Active Usersが通常の2倍以上、TPS急増',
        },
      ],
      solutions: [
        {
          title: '現在のアクティブトランザクションの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: 'ボトルネックトランザクションの特定',
        },
        {
          title: 'Slow Transactionのクエリ最適化',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜3日',
          expectedEffect: 'トランザクション応答時間の50〜70%改善',
        },
        {
          title: 'Thread Poolサイズの調整',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: '同時処理容量の増加',
        },
      ],
    },
    app_tps: {
      type: 'metric',
      threshold: 'both',
      description:
        '秒間トランザクション処理量（TPS）が正常範囲を逸脱しました。\n急激な増加はトラフィック急増、急激な減少はシステム障害を意味する可能性があります。',
      mainCauses: [
        {
          cause: 'トラフィック急増',
          priority: 'High',
          explanation: 'イベントやマーケティングキャンペーンなどによる急激なユーザー流入',
          diagnosticCriteria: 'TPSが通常の200%以上に増加',
        },
        {
          cause: 'DDoS攻撃',
          priority: 'Critical',
          explanation: '悪意のある大量リクエストによるTPSの異常増加',
          diagnosticCriteria: 'TPS急増だが異常パターン、Error Rate増加',
        },
        {
          cause: 'システム障害（TPS急落）',
          priority: 'Critical',
          explanation: 'WASまたはDB障害によりトランザクション処理不可',
          diagnosticCriteria: 'TPSが通常の50%以下、Error Rate増加',
        },
      ],
      solutions: [
        {
          title: 'トラフィックパターンの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: '正常/異常トラフィックの判別',
        },
        {
          title: 'Auto Scalingの適用',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: 'トラフィック増加への自動対応',
        },
        {
          title: 'Rate Limitingの適用',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '4〜8時間',
          expectedEffect: 'DDoSおよび過剰リクエストの遮断',
        },
      ],
    },
    app_transaction_count: {
      type: 'metric',
      threshold: 'both',
      description:
        '分間トランザクション処理総量が異常です。\n急激な変化はシステムの問題を示している可能性があります。',
      mainCauses: [
        {
          cause: 'ピーク時間帯の到来',
          priority: 'Low',
          explanation: '定期的なピーク時間による自然な増加',
          diagnosticCriteria: '周期的なパターン、Error Rateは正常',
        },
        {
          cause: 'バッチ処理の実行',
          priority: 'Medium',
          explanation: '大量バッチ処理の実行によるThroughputの急増',
          diagnosticCriteria: '特定の時間帯に集中、特定のTransaction Typeに集中',
        },
        {
          cause: 'WASインスタンスのダウン',
          priority: 'Critical',
          explanation: '一部のWASインスタンス障害による全体スループットの低下',
          diagnosticCriteria: 'Active WAS数の減少、Error Rateの増加',
        },
      ],
      solutions: [
        {
          title: 'WASインスタンスの状態確認',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '5〜10分',
          expectedEffect: '障害インスタンスの特定',
        },
        {
          title: 'バッチ処理スケジュールの調整',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'ピーク時間の分散',
        },
        {
          title: 'ロードバランシングポリシーの見直し',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2〜4時間',
          expectedEffect: '負荷の均等分散',
        },
      ],
    },
    app_transaction_elapse_time_avg: {
      type: 'metric',
      threshold: 'high',
      description:
        '平均トランザクション応答時間が閾値を超過しました。\nユーザーエクスペリエンスの低下およびパフォーマンスの問題が発生しています。',
      mainCauses: [
        {
          cause: 'DBクエリパフォーマンスの低下',
          priority: 'High',
          explanation: 'Slow Queryの増加による全体応答時間の増加',
          diagnosticCriteria: 'SQL Elapse Time Avgの増加',
        },
        {
          cause: '外部API応答遅延',
          priority: 'High',
          explanation: '連携する外部システムの応答遅延',
          diagnosticCriteria: 'Remote Call Elapse Timeの増加',
        },
        {
          cause: 'GC過負荷',
          priority: 'Medium',
          explanation: '頻繁なGCによるアプリケーション応答遅延',
          diagnosticCriteria: 'GC Timeの増加、Heap Usageが高い',
        },
      ],
      solutions: [
        {
          title: 'APMトレース分析によるボトルネック区間の特定',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: '応答時間の遅延区間を正確に把握',
        },
        {
          title: 'Slow Queryの最適化',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '1〜5日',
          expectedEffect: '応答時間の40〜60%改善',
        },
        {
          title: 'Cacheの適用',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1〜3日',
          expectedEffect: 'DB負荷の軽減、応答時間の30〜50%改善',
        },
      ],
    },
    app_transaction_elapse_time: {
      type: 'metric',
      threshold: 'high',
      description:
        'トランザクション応答時間の合計が異常に増加しました。\n全体的なシステムパフォーマンスの低下を示しています。',
      mainCauses: [
        {
          cause: 'トランザクション処理量の増加',
          priority: 'Medium',
          explanation: '正常なトラフィック増加による合計の増加',
          diagnosticCriteria: 'TPS増加、平均応答時間は正常',
        },
        {
          cause: '個別トランザクションの遅延',
          priority: 'High',
          explanation: '各トランザクションの応答時間が増加',
          diagnosticCriteria: '平均応答時間も同時に増加',
        },
        {
          cause: '特定トランザクションの滞留',
          priority: 'High',
          explanation: '一部の遅いトランザクションが全体の合計を増加させている',
          diagnosticCriteria: '特定のURLパターンで極端に高い応答時間',
        },
      ],
      solutions: [
        {
          title: 'トランザクション別の応答時間分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '20〜40分',
          expectedEffect: '問題のあるトランザクションの特定',
        },
        {
          title: '遅いトランザクションの最適化',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜5日',
          expectedEffect: '全体応答時間合計の30〜50%削減',
        },
      ],
    },
    app_transaction_exception_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'トランザクションエラーが閾値を超過しました。\nビジネスロジックのエラーやシステム障害が発生しています。',
      mainCauses: [
        {
          cause: 'DB接続失敗',
          priority: 'Critical',
          explanation: 'DB Connection Poolの枯渇またはDBサーバー障害',
          diagnosticCriteria: 'JDBC Exceptionが多数発生',
        },
        {
          cause: '外部API障害',
          priority: 'High',
          explanation: '連携する外部システムの障害によるエラー',
          diagnosticCriteria: 'Timeout Exception、Connection Refusedの増加',
        },
      ],
      solutions: [
        {
          title: 'エラーログおよびスタックトレースの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: 'エラー原因の特定',
        },
        {
          title: '緊急ロールバック（デプロイ後に発生した場合）',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: 'サービスの正常化',
        },
        {
          title: 'バグ修正および再デプロイ',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '数時間〜数日',
          expectedEffect: '根本原因の解決',
        },
      ],
    },
    app_txn_error_ratio: {
      type: 'metric',
      threshold: 'high',
      description:
        '全トランザクションに対するエラー比率が閾値を超過しました。\nサービス品質が低下しています。',
      mainCauses: [
        {
          cause: 'システムリソース不足',
          priority: 'High',
          explanation: 'CPU、Memory、Threadの不足によるリクエスト処理失敗',
          diagnosticCriteria: 'CPU Usage > 90%、Memory不足',
        },
        {
          cause: '依存サービスの障害',
          priority: 'High',
          explanation: 'DB、Cache、外部APIなどの依存サービスの障害',
          diagnosticCriteria: '特定サービスの呼び出し時にエラーが集中',
        },
        {
          cause: 'コード品質の問題',
          priority: 'Medium',
          explanation: 'Exception処理の不備、エッジケース処理の不足',
          diagnosticCriteria: '多様なExceptionタイプが発生',
        },
      ],
      solutions: [
        {
          title: 'Errorパターンの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: '共通原因の特定',
        },
        {
          title: 'リソースの増設',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1〜4時間',
          expectedEffect: 'システム余裕の確保',
        },
      ],
    },
    app_txn_error_count: {
      type: 'metric',
      threshold: 'high',
      description: 'エラートランザクション発生量が閾値を超過しました。',
      mainCauses: [
        {
          cause: '全体トラフィックの増加',
          priority: 'High',
          explanation: 'トラフィック増加に比例したエラーの増加',
          diagnosticCriteria: 'TPS増加、エラー率は正常範囲',
        },
        {
          cause: '特定機能の障害',
          priority: 'Medium',
          explanation: '一部の機能で継続的にエラーが発生',
          diagnosticCriteria: '特定のAPIエンドポイントに集中',
        },
      ],
      solutions: [
        {
          title: 'エラー発生APIエンドポイントの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '15〜30分',
          expectedEffect: '問題のある機能の特定',
        },
        {
          title: '該当機能の緊急パッチ',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1〜8時間',
          expectedEffect: 'エラー発生の停止',
        },
      ],
    },
    app_remote_elapse: {
      type: 'metric',
      threshold: 'high',
      description:
        'Remote Call応答時間の合計が増加しました。\n外部システム連携のパフォーマンス低下を示しています。',
      mainCauses: [
        {
          cause: '外部API応答遅延',
          priority: 'High',
          explanation: '連携している外部システムの性能低下',
          diagnosticCriteria: 'Remote Call平均応答時間の増加',
        },
        {
          cause: 'Remote Call呼び出し頻度の増加',
          priority: 'Medium',
          explanation: 'ビジネスロジックの変更による外部呼び出しの増加',
          diagnosticCriteria: 'Remote Call Countの増加',
        },
        {
          cause: 'ネットワーク遅延',
          priority: 'Medium',
          explanation: 'ネットワーク品質低下による遅延',
          diagnosticCriteria: 'ネットワークLatencyの増加',
        },
      ],
      solutions: [
        {
          title: '外部APIの応答時間モニタリング',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜20分',
          expectedEffect: '遅延が発生している外部システムの特定',
        },
        {
          title: 'Timeout設定およびCircuit Breakerの適用',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: '障害の伝播防止',
        },
        {
          title: 'Cacheの適用（可能な場合）',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2〜3日',
          expectedEffect: '外部呼び出し頻度の削減',
        },
      ],
    },
    app_sql_exec_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'SQL実行回数がしきい値を超えました。\nDB負荷の増大およびパフォーマンス低下が発生する可能性があります。',
      mainCauses: [
        {
          cause: 'N+1 Query問題',
          priority: 'High',
          explanation: 'ループ内での個別クエリ実行によるクエリ数の急増',
          diagnosticCriteria: '特定のトランザクションで同一クエリが多数実行',
        },
        {
          cause: 'トラフィックの増加',
          priority: 'Medium',
          explanation: '正常なトラフィック増加に伴うクエリの増加',
          diagnosticCriteria: 'TPSの増加に比例',
        },
        {
          cause: '非効率なデータ照会',
          priority: 'Medium',
          explanation: '必要以上のデータを複数回照会',
          diagnosticCriteria: '同一データの繰り返し照会',
        },
      ],
      solutions: [
        {
          title: 'SQL実行パターンの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'N+1 Queryの特定',
        },
        {
          title: 'Query最適化（JOIN、Batch Query）',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜3日',
          expectedEffect: 'SQL実行回数を70〜90%削減',
        },
        {
          title: 'Cacheの適用',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2〜3日',
          expectedEffect: 'DB負荷を30〜50%削減',
        },
      ],
    },
    app_sql_prepare_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'SQL Prepare回数が過多です。\nPreparedStatementの再利用不足またはクエリ生成の問題があります。',
      mainCauses: [
        {
          cause: 'PreparedStatementの未再利用',
          priority: 'High',
          explanation: '毎回新しいPreparedStatementを生成',
          diagnosticCriteria: 'Prepare CountとExecute Countの比率が1:1に近い',
        },
        {
          cause: 'Connection Pool設定の不適切',
          priority: 'Medium',
          explanation: 'Connectionの頻繁な生成・解放',
          diagnosticCriteria: 'Connectionの再利用率が低い',
        },
      ],
      solutions: [
        {
          title: 'PreparedStatement再利用構造の改善',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜5日',
          expectedEffect: 'Prepare回数を50〜70%削減',
        },
        {
          title: 'Connection Pool設定の最適化',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: 'Connection再利用率の向上',
        },
      ],
    },
    app_sql_fetch_count: {
      type: 'metric',
      threshold: 'high',
      description:
        'SQL Fetch回数が非常に多くなっています。\n大量のデータを照会しており、パフォーマンス低下およびメモリの過剰使用が発生する可能性があります。',
      mainCauses: [
        {
          cause: 'ページング未適用',
          priority: 'High',
          explanation: '全データを一度に照会',
          diagnosticCriteria: 'LIMIT句のないクエリ、ResultSetのサイズが非常に大きい',
        },
        {
          cause: '不要なカラムの照会',
          priority: 'Medium',
          explanation: 'SELECT *の使用による全カラムの照会',
          diagnosticCriteria: '実際に使用しているカラムは一部のみ',
        },
        {
          cause: 'Batch処理の不足',
          priority: 'Medium',
          explanation: '大量データ処理時に適切なBatchが未適用',
          diagnosticCriteria: '1件ずつFetch',
        },
      ],
      solutions: [
        {
          title: 'ページングの適用',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1〜2日',
          expectedEffect: 'Fetch回数およびメモリ使用量を80%削減',
        },
        {
          title: '必要なカラムのみ照会（SELECT句の最適化）',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1〜3日',
          expectedEffect: 'ネットワークトラフィックおよびメモリを30〜50%削減',
        },
        {
          title: 'Fetch Sizeの調整',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: 'DB-WAS間のRound Tripを削減',
        },
      ],
    },
    app_sql_elapse_time_avg: {
      type: 'metric',
      threshold: 'high',
      description:
        '平均SQL実行時間が遅延しています。\nクエリの最適化またはDBリソースの確認が必要です。',
      mainCauses: [
        {
          cause: 'インデックスの欠如',
          priority: 'High',
          explanation: 'WHERE条件にインデックスがなくFull Table Scanが発生',
          diagnosticCriteria: '実行計画でTable Scan',
        },
        {
          cause: '複雑なJOIN',
          priority: 'High',
          explanation: '多重JOINによるクエリ複雑度の増加',
          diagnosticCriteria: 'JOINが3つ以上、大容量テーブル',
        },
        {
          cause: 'DBリソース不足',
          priority: 'Medium',
          explanation: 'DB CPU、Memory、Disk I/Oの不足',
          diagnosticCriteria: 'DBサーバーのリソース使用率が80%以上',
        },
      ],
      solutions: [
        {
          title: 'Slow Queryの分析および実行計画の確認',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: '問題クエリの特定',
        },
        {
          title: '必要なカラムのみ照会（SELECT句の最適化）',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: 'クエリ実行時間を70〜90%改善',
        },
        {
          title: 'クエリのリファクタリング（JOINの最小化、サブクエリの除去）',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '3〜7日',
          expectedEffect: 'クエリパフォーマンスを50〜80%改善',
        },
      ],
    },
    app_sql_elapse_time: {
      type: 'metric',
      threshold: 'high',
      description: 'SQL実行時間の合計が増加しました。\n全体的なDB負荷の増大を示しています。',
      mainCauses: [
        {
          cause: 'SQL実行回数の増加',
          priority: 'Medium',
          explanation: 'トラフィック増加によるクエリ実行頻度の増加',
          diagnosticCriteria: 'SQL Execute Countの増加',
        },
        {
          cause: '個別クエリのパフォーマンス低下',
          priority: 'High',
          explanation: '各クエリの実行時間が増加',
          diagnosticCriteria: 'SQL Elapse Avgの増加',
        },
      ],
      solutions: [
        {
          title: 'SQL実行パターンおよびパフォーマンスの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: '原因の把握（頻度 vs パフォーマンス）',
        },
        {
          title: 'クエリの最適化',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜5日',
          expectedEffect: '全体SQL時間を40〜60%削減',
        },
      ],
    },
    app_connection_time: {
      type: 'metric',
      threshold: 'high',
      description: 'DB接続時間の合計が増加しました。\nConnection取得の遅延が発生しています。',
      mainCauses: [
        {
          cause: 'Connection Poolの不足',
          priority: 'High',
          explanation: 'PoolからのConnection取得待ち時間の増加',
          diagnosticCriteria: 'Active ConnectionsがMax Pool Sizeに近い',
        },
        {
          cause: 'DBネットワーク遅延',
          priority: 'Medium',
          explanation: 'DBサーバーとのネットワーク遅延',
          diagnosticCriteria: 'ネットワークLatencyの増加',
        },
        {
          cause: 'Connection Leak',
          priority: 'High',
          explanation: '使用後に返却されないConnectionによるPoolの枯渇',
          diagnosticCriteria: 'Connection使用後の未返却',
        },
      ],
      solutions: [
        {
          title: 'Connection Poolのモニタリング',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜20分',
          expectedEffect: 'Pool状態の把握',
        },
        {
          title: 'Connection Poolサイズの拡張',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'Connection待ち時間の削減',
        },
        {
          title: 'Connection Leakの検出および修正',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1〜3日',
          expectedEffect: 'Pool枯渇の防止',
        },
      ],
    },
    app_http_call: {
      type: 'metric',
      threshold: 'high',
      description:
        'Remote Call呼び出し回数が増加しました。\n外部システムへの依存度が高まっています。',
      mainCauses: [
        {
          cause: 'ビジネスロジックの変更',
          priority: 'Medium',
          explanation: '新規機能による外部API呼び出しの増加',
          diagnosticCriteria: '特定機能のデプロイ後に発生',
        },
        {
          cause: 'N+1 Remote Call',
          priority: 'High',
          explanation: 'ループ内での外部API呼び出し',
          diagnosticCriteria: '同一APIを複数回呼び出し',
        },
        {
          cause: 'トラフィックの増加',
          priority: 'Low',
          explanation: '正常なトラフィック増加に比例',
          diagnosticCriteria: 'TPSの増加に比例',
        },
      ],
      solutions: [
        {
          title: 'Remote Callパターンの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'N+1問題の特定',
        },
        {
          title: 'Batch API呼び出しへの変更',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜4日',
          expectedEffect: 'Remote Call回数を70〜90%削減',
        },
        {
          title: 'Cacheの適用',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2〜3日',
          expectedEffect: '外部呼び出しを30〜50%削減',
        },
      ],
    },
    app_active_user: {
      type: 'metric',
      threshold: 'high',
      description:
        'アクティブユーザー数が急増しました。\nトラフィックの急増またはイベントによる流入が発生しています。',
      mainCauses: [
        {
          cause: 'マーケティングキャンペーン',
          priority: 'Low',
          explanation: '計画されたイベントによる自然な増加',
          diagnosticCriteria: '事前告知されたイベントの時間帯',
        },
        {
          cause: 'メディア露出',
          priority: 'Medium',
          explanation: 'ニュース、SNSなどを通じた急激な流入',
          diagnosticCriteria: '予期しないトラフィックの急増',
        },
        {
          cause: 'Bot攻撃',
          priority: 'High',
          explanation: '自動化されたBotによる異常なアクセス',
          diagnosticCriteria: '異常なアクセスパターン',
        },
      ],
      solutions: [
        {
          title: 'トラフィックソースおよびパターンの分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: '正常/異常トラフィックの区別',
        },
        {
          title: 'Auto Scalingの有効化',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '即時',
          expectedEffect: 'システム容量の自動拡張',
        },
        {
          title: 'Botのブロック（異常な場合）',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜4時間',
          expectedEffect: '異常トラフィックのブロック',
        },
      ],
    },
    app_concurrent_user: {
      type: 'metric',
      threshold: 'high',
      description:
        '同時接続ユーザー数が急増しました。\nシステムリソースの不足が発生する可能性があります。',
      mainCauses: [
        {
          cause: 'ピーク時間帯',
          priority: 'Low',
          explanation: '定期的なピーク時間帯（昼食時、夕方など）',
          diagnosticCriteria: '周期的パターン',
        },
        {
          cause: 'イベント開始',
          priority: 'Medium',
          explanation: '限定販売、チケットオープンなどによる同時集中',
          diagnosticCriteria: '特定時刻に集中',
        },
        {
          cause: 'セッションTimeout設定過多',
          priority: 'Medium',
          explanation: 'セッションが長時間維持され蓄積',
          diagnosticCriteria: 'セッションTimeoutが長すぎる',
        },
      ],
      solutions: [
        {
          title: 'セッションTimeout設定の見直し',
          priority: 'Medium',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: '不要なセッションの整理',
        },
        {
          title: 'Session Storage拡張（Redisなど）',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: 'セッション処理容量の増加',
        },
      ],
    },
    app_apdex: {
      type: 'metric',
      threshold: 'low',
      description:
        'Apdex（Application Performance Index）スコアが低下しています。\nユーザー体験が悪化しています。',
      mainCauses: [
        {
          cause: '応答時間の増加',
          priority: 'High',
          explanation: 'Transaction Elapse Timeの増加による満足度低下',
          diagnosticCriteria: '平均応答時間 > 3秒',
        },
        {
          cause: 'エラー発生の増加',
          priority: 'High',
          explanation: 'エラーによるユーザー不満足',
          diagnosticCriteria: 'Error Transaction Ratio > 1%',
        },
        {
          cause: 'タイムアウトの増加',
          priority: 'Medium',
          explanation: 'リクエストが完了せずタイムアウト',
          diagnosticCriteria: 'Timeout Exceptionの増加',
        },
      ],
      solutions: [
        {
          title: 'Apdex Bad Countトランザクション分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: '不満足原因の特定',
        },
        {
          title: '応答時間の改善（クエリ最適化、Cacheなど）',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜5日',
          expectedEffect: 'Apdexスコアの向上',
        },
      ],
    },
    app_apdex_excellent: {
      type: 'metric',
      threshold: 'low',
      description:
        '優秀応答トランザクション数が減少しました。\n高速な応答を提供するトランザクションが減少しています。',
      mainCauses: [
        {
          cause: '全体的なパフォーマンス低下',
          priority: 'High',
          explanation: 'システム全体の応答時間が遅延',
          diagnosticCriteria: '平均応答時間の増加',
        },
        {
          cause: 'トラフィックの増加',
          priority: 'Medium',
          explanation: '負荷増加による応答時間の増加',
          diagnosticCriteria: 'TPSの増加',
        },
      ],
      solutions: [
        {
          title: 'パフォーマンス低下原因の分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '1〜2時間',
          expectedEffect: 'ボトルネック箇所の把握',
        },
        {
          title: 'システム最適化',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '3〜7日',
          expectedEffect: 'Excellent Countの増加',
        },
      ],
    },
    app_apdex_warning: {
      type: 'metric',
      threshold: 'high',
      description:
        '警告レベルの応答トランザクションが増加しました。\n応答時間が目標値を超過しています。',
      mainCauses: [
        {
          cause: '一部トランザクションの遅延',
          priority: 'High',
          explanation: '特定のトランザクションが遅延',
          diagnosticCriteria: '特定のURLパターンで遅延',
        },
        {
          cause: 'システムリソース不足',
          priority: 'Medium',
          explanation: 'CPU、メモリ不足による応答遅延',
          diagnosticCriteria: 'リソース使用率80%以上',
        },
      ],
      solutions: [
        {
          title: 'Warningレベルトランザクション分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: '遅延トランザクションの特定',
        },
        {
          title: '該当トランザクションの最適化',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '2〜5日',
          expectedEffect: 'Warning Countの削減',
        },
      ],
    },
    app_apdex_bad: {
      type: 'metric',
      threshold: 'high',
      description: '不良応答トランザクションが増加しました。\n深刻な応答遅延が発生しています。',
      mainCauses: [
        {
          cause: '深刻なパフォーマンス問題',
          priority: 'Critical',
          explanation: 'システム障害レベルの応答遅延',
          diagnosticCriteria: '応答時間 > 12秒（Apdex Tの4倍）',
        },
        {
          cause: '特定機能の障害',
          priority: 'High',
          explanation: '一部機能で極端な遅延',
          diagnosticCriteria: '特定URLに集中発生',
        },
      ],
      solutions: [
        {
          title: 'Bad Countトランザクションの緊急分析',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: '緊急対処が必要な項目の特定',
        },
        {
          title: '緊急対処（該当機能の一時停止を検討）',
          priority: 'Critical',
          difficulty: 'Easy',
          estimatedTime: '即時',
          expectedEffect: 'システム全体の保護',
        },
      ],
    },
    app_was_memory_total: {
      type: 'metric',
      threshold: 'high',
      description: 'アプリケーション全体のメモリ使用量が過大です。',
      mainCauses: [
        {
          cause: 'メモリリーク',
          priority: 'High',
          explanation: '使用されていないオブジェクトがメモリに保持され続けている',
          diagnosticCriteria: 'メモリ使用量の持続的な増加',
        },
        {
          cause: '大量データ処理',
          priority: 'Medium',
          explanation: '一度に大量のデータをメモリにロード',
          diagnosticCriteria: '特定処理実行時にメモリが急増',
        },
      ],
      solutions: [
        {
          title: 'ヒープダンプ分析',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜3時間',
          expectedEffect: 'メモリを消費しているオブジェクトの特定',
        },
        {
          title: 'メモリリークの修正',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2〜7日',
          expectedEffect: 'メモリ使用量の安定化',
        },
      ],
    },
    app_was_memory_rss: {
      type: 'metric',
      threshold: 'high',
      description:
        'アプリケーションのRSS（Resident Set Size）メモリが過大です。\n実際の物理メモリ使用量が高い状態です。',
      mainCauses: [
        {
          cause: 'Heap外メモリ使用量の増加',
          priority: 'High',
          explanation: 'Native Memory、Direct Bufferなどの使用',
          diagnosticCriteria: 'Heapは正常だがRSSが高い',
        },
        {
          cause: 'JVM Off-Heapメモリ',
          priority: 'Medium',
          explanation: 'Metaspace、Code Cacheなどの増加',
          diagnosticCriteria: 'Metaspace使用量の増加',
        },
      ],
      solutions: [
        {
          title: 'Native Memory Trackingの有効化',
          priority: 'High',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: 'Off-Heapメモリ使用状況の把握',
        },
        {
          title: 'Direct Buffer使用の見直し',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2〜4日',
          expectedEffect: 'Native Memory使用の最適化',
        },
      ],
    },
    app_pod_cpu_usage: {
      type: 'metric',
      threshold: 'high',
      description:
        'Kubernetes PodのCPU使用率が過大です。\nPodのCPUリソース制限に到達する可能性があります。',
      mainCauses: [
        {
          cause: 'CPU Limit設定不足',
          priority: 'High',
          explanation: 'Podに割り当てられたCPUが不足',
          diagnosticCriteria: 'CPU Request/Limitが実際の必要量より小さい',
        },
        {
          cause: 'アプリケーションのCPU過剰使用',
          priority: 'High',
          explanation: 'アプリケーション自体のCPU使用問題',
          diagnosticCriteria: 'Application CPU Usageも高い',
        },
      ],
      solutions: [
        {
          title: 'Pod CPU Limitの増設',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'CPU Throttlingの防止',
        },
        {
          title: 'HPA（Horizontal Pod Autoscaler）の設定',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '1〜2日',
          expectedEffect: '負荷に応じた自動スケーリング',
        },
      ],
    },
    app_pod_memory_usage: {
      type: 'metric',
      threshold: 'high',
      description: 'Kubernetes Podのメモリ使用率が過大です。\nOOMKilledのリスクがあります。',
      mainCauses: [
        {
          cause: 'Memory Limit設定不足',
          priority: 'High',
          explanation: 'Podに割り当てられたメモリが不足',
          diagnosticCriteria: 'Memory Limitが実際の必要量より小さい',
        },
        {
          cause: 'アプリケーションのメモリリーク',
          priority: 'High',
          explanation: 'アプリケーションのメモリリーク',
          diagnosticCriteria: 'Application Memoryも高い',
        },
      ],
      solutions: [
        {
          title: 'Pod Memory Limitの増設',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'OOMKilledの防止',
        },
        {
          title: 'アプリケーションメモリの最適化',
          priority: 'High',
          difficulty: 'Hard',
          estimatedTime: '2〜7日',
          expectedEffect: '根本的なメモリ使用量の削減',
        },
      ],
    },
    app_db_active_connection_count: {
      type: 'metric',
      threshold: 'high',
      description: 'アクティブDBコネクション数が過大です。Connection Pool枯渇のリスクがあります。',
      mainCauses: [
        {
          cause: 'Long Running Query',
          priority: 'High',
          explanation: '長時間実行されるクエリによるConnection占有時間の増加',
          diagnosticCriteria: 'SQL Elapse Timeが高い',
        },
        {
          cause: 'Connection Leak',
          priority: 'Critical',
          explanation: 'Connection使用後に未返却',
          diagnosticCriteria: 'Connectionが増加し続けている',
        },
        {
          cause: 'トラフィックの増加',
          priority: 'Medium',
          explanation: 'リクエスト増加によるDB Connection需要の増加',
          diagnosticCriteria: 'TPSの増加',
        },
      ],
      solutions: [
        {
          title: '現在のアクティブConnection分析',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '10〜30分',
          expectedEffect: 'Long Running Queryの特定',
        },
        {
          title: 'Connection Leakの点検および修正',
          priority: 'Critical',
          difficulty: 'Medium',
          estimatedTime: '1〜3日',
          expectedEffect: 'Connection Poolの安定化',
        },
        {
          title: 'Connection Poolサイズの増設',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'Pool余裕の確保',
        },
      ],
    },
    app_db_total_connection_count: {
      type: 'metric',
      threshold: 'high',
      description:
        '全DB コネクション数がPool Sizeに近づいています。\n新規Connection作成不可のリスクがあります。',
      mainCauses: [
        {
          cause: 'Connection Poolサイズ不足',
          priority: 'High',
          explanation: '実際の必要量よりPool Sizeが小さい',
          diagnosticCriteria: '頻繁にMax Pool Sizeに到達',
        },
        {
          cause: 'Connection返却の遅延',
          priority: 'Medium',
          explanation: 'クエリ実行後のConnection返却が遅い',
          diagnosticCriteria: 'Average Connection Hold Timeが高い',
        },
      ],
      solutions: [
        {
          title: 'Connection Pool設定の見直し',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: '適正Pool Sizeの把握',
        },
        {
          title: 'Connection Poolサイズの増設',
          priority: 'High',
          difficulty: 'Easy',
          estimatedTime: '30分〜1時間',
          expectedEffect: 'Connection不足の解消',
        },
        {
          title: 'Connection使用の最適化（早期返却）',
          priority: 'Medium',
          difficulty: 'Medium',
          estimatedTime: '2〜5日',
          expectedEffect: 'Connection回転率の向上',
        },
      ],
    },
  },
  database: {},
};

export const ALERT_INSIGHT_DATA: Record<'en' | 'ko' | 'ja', AlertInsightMap> = {
  ko: ALERT_INSIGHT_KO_DATA,
  en: ALERT_INSIGHT_EN_DATA,
  ja: ALERT_INSIGHT_JA_DATA,
};
